import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import config from "./config";
import Dashboard from "./Dashboard";
import DocumentsPanel from "./DocumentsPanel";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";

const userPool = new CognitoUserPool({
  UserPoolId: config.cognito.userPoolId,
  ClientId: config.cognito.clientId,
});

const MAX_MODELS = 5;

const PROVIDER_ICONS = {
  "Amazon": "🟠", "Anthropic": "🟣", "Meta": "🔵", "Mistral AI": "🟡",
  "Cohere": "🟢", "DeepSeek": "⚫", "Writer": "✍️", "Other": "⬜",
};

const GREETING = { role: "assistant", text: "👋 Hi there! I'm your **AWS Solutions Architect** assistant, powered by Amazon Bedrock.\n\nI can help you with:\n\n- 🏗️ Architecture design and reviews\n- 💰 Cost optimization strategies\n- 🔒 Security best practices\n- 📋 AWS Well-Architected Framework guidance\n- 🚀 Migration planning and strategies\n- ☁️ Any AWS service questions\n\nSelect a model from the dropdown above and ask me anything!" };

const SUGGESTIONS = [
  { icon: "🏗️", text: "Explain the AWS Well-Architected Framework" },
  { icon: "💰", text: "How can I reduce my AWS bill?" },
  { icon: "🔒", text: "What are AWS security best practices?" },
  { icon: "🚀", text: "Design a serverless architecture for a web app" },
  { icon: "☁️", text: "Compare ECS vs EKS for container workloads" },
  { icon: "🛡️", text: "How do I set up a multi-account AWS Organization?" },
];

const BotIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="10" rx="2" /><circle cx="8.5" cy="16" r="1.5" /><circle cx="15.5" cy="16" r="1.5" />
    <path d="M12 11V7" /><circle cx="12" cy="5" r="2" /><path d="M1 16h2" /><path d="M21 16h2" />
  </svg>
);
const SendIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" /></svg>
);
const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.7 }}>
    <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
  </svg>
);
const SparkleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.7 }}>
    <path d="M12 2l2.4 7.2H22l-6 4.8 2.4 7.2L12 16.4l-6.4 4.8L8 14 2 9.2h7.6z" />
  </svg>
);
const AttachIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
  </svg>
);

function AnimatedBar({ pct, color, style = {} }) {
  const [w, setW] = React.useState(0);
  React.useEffect(() => { setW(0); const id = requestAnimationFrame(() => setW(pct)); return () => cancelAnimationFrame(id); }, [pct]);
  return <div style={{ width: `${w}%`, transition: "width 0.8s ease-out", backgroundColor: color, ...style }} />;
}

// Simple word-level diff for prompt comparison
function wordDiff(oldText, newText) {
  const oldWords = oldText.split(/(\s+)/);
  const newWords = newText.split(/(\s+)/);
  const result = [];
  // LCS-based diff (simplified for reasonable prompt lengths)
  const m = oldWords.length, n = newWords.length;
  // For very long texts, fall back to line-level diff
  if (m * n > 500000) {
    const oldLines = oldText.split("\n"), newLines = newText.split("\n");
    const oldSet = new Set(oldLines.map(l => l.trim()));
    const newSet = new Set(newLines.map(l => l.trim()));
    for (const line of oldLines) { if (!newSet.has(line.trim())) result.push({ type: "del", text: line + "\n" }); }
    for (const line of newLines) {
      if (!oldSet.has(line.trim())) result.push({ type: "add", text: line + "\n" });
      else result.push({ type: "same", text: line + "\n" });
    }
    return result;
  }
  // Build LCS table
  const dp = Array.from({ length: m + 1 }, () => new Uint16Array(n + 1));
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = oldWords[i-1] === newWords[j-1] ? dp[i-1][j-1] + 1 : Math.max(dp[i-1][j], dp[i][j-1]);
  // Backtrack
  let i = m, j = n;
  const ops = [];
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldWords[i-1] === newWords[j-1]) { ops.push({ type: "same", text: oldWords[i-1] }); i--; j--; }
    else if (j > 0 && (i === 0 || dp[i][j-1] >= dp[i-1][j])) { ops.push({ type: "add", text: newWords[j-1] }); j--; }
    else { ops.push({ type: "del", text: oldWords[i-1] }); i--; }
  }
  return ops.reverse();
}

function App() {
  const [token, setToken] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [messages, setMessages] = useState([GREETING]);
  const [loading, setLoading] = useState(false);
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem("sa-theme");
    return saved ? saved === "dark" : true;
  });
  const [loggedInEmail, setLoggedInEmail] = useState("");
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showModelMenu, setShowModelMenu] = useState(false);
  const [selectedModels, setSelectedModels] = useState(["us.amazon.nova-pro-v1:0", "us.amazon.nova-2-lite-v1:0", "us.anthropic.claude-sonnet-4-6"]);
  const [modelGroups, setModelGroups] = useState({});
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState("You are an expert AWS Solutions Architect assistant powered by Amazon Bedrock. You have deep expertise across all AWS services, architectures, and best practices. You can help with architecture design, cost optimization, security, Well-Architected reviews, migration strategies, and any AWS-related questions. Keep your replies concise — no more than 250 words. Be helpful and friendly.");
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState("");
  const [promptDirty, setPromptDirty] = useState(false);
  const [judgeModel, setJudgeModel] = useState("us.anthropic.claude-sonnet-4-6");
  const [ragEnabled, setRagEnabled] = useState(false);
  const [useFewShot, setUseFewShot] = useState(false);
  const [fewShotText, setFewShotText] = useState("");
  const [editingFewShot, setEditingFewShot] = useState("");
  const [fewShotDirty, setFewShotDirty] = useState(false);
  const [autoOptimize, setAutoOptimize] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizedPrompts, setOptimizedPrompts] = useState({});  // {modelId: optimizedPrompt}
  const DEFAULT_FEW_SHOT = `Here are examples of how to structure your responses:

**User:** What is Amazon S3?
**Response:**
## Overview
Amazon S3 (Simple Storage Service) is an object storage service offering scalability, data availability, security, and performance.

## Key Features
- **Durability**: 99.999999999% (11 nines) durability
- **Storage Classes**: S3 Standard, Intelligent-Tiering, Glacier, and more
- **Security**: Encryption, access policies, and VPC endpoints

## Common Use Cases
1. Data lakes and analytics
2. Backup and disaster recovery
3. Static website hosting

## Pricing Considerations
Pay only for what you use — storage, requests, and data transfer.

---

**User:** How do I secure my AWS account?
**Response:**
## Immediate Actions
1. **Enable MFA** on the root account and all IAM users
2. **Create IAM users** — never use root for daily tasks
3. **Apply least privilege** — grant only permissions needed

## Best Practices
- Enable **CloudTrail** for audit logging
- Use **AWS Organizations** with SCPs for guardrails
- Enable **GuardDuty** for threat detection
- Review **IAM Access Analyzer** findings regularly

## Tools & Services
| Service | Purpose |
|---------|---------|
| IAM | Identity and access management |
| CloudTrail | API activity logging |
| Config | Resource compliance tracking |
| Security Hub | Centralized security findings |

## Next Steps
Run a **Well-Architected Security Pillar** review to identify gaps.`;
  const DEFAULT_PROMPT = "You are an expert AWS Solutions Architect assistant powered by Amazon Bedrock. You have deep expertise across all AWS services, architectures, and best practices. You can help with architecture design, cost optimization, security, Well-Architected reviews, migration strategies, and any AWS-related questions. Keep your replies concise — no more than 250 words. Be helpful and friendly.";
  const RAG_PROMPT = "You are an expert AWS Solutions Architect assistant powered by Amazon Bedrock with access to a curated knowledge base. Answer questions based EXCLUSIVELY on the retrieved context provided. Do not use your general knowledge to supplement the answer. If the retrieved context does not contain enough information, clearly state that the documents do not contain sufficient information and explain what is missing. Keep your replies concise — no more than 250 words. Be helpful and friendly.";
  const [showSidebar, setShowSidebar] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(320);
  const sidebarDragRef = useRef(null);
  const [modelSearch, setModelSearch] = useState("");
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [currentTab, setCurrentTab] = useState("chat");
  const [dashRange, setDashRange] = useState("7d");
  const [dashCustomStart, setDashCustomStart] = useState("");
  const [dashCustomEnd, setDashCustomEnd] = useState("");
  const [dashRefreshKey, setDashRefreshKey] = useState(0);
  const [dashHasOptimized, setDashHasOptimized] = useState(false);
  const [dashMetrics, setDashMetrics] = useState(new Set([
    "accuracy", "relevance", "helpfulness", "faithfulness", "following_instructions",
    "logical_coherence", "harmfulness", "stereotyping", "refusal",
    "professional_style_and_tone", "readability",
  ]));
  const [dashModelFilter, setDashModelFilter] = useState([]);
  const [showDashModelDropdown, setShowDashModelDropdown] = useState(false);
  const [dashModelSearch, setDashModelSearch] = useState("");
  const [sessionLoading, setSessionLoading] = useState(true);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("sa-sessionId") || null);
  const [sessions, setSessions] = useState([]);
  const [showSessionDropdown, setShowSessionDropdown] = useState(false);
  const [editingSessionTitle, setEditingSessionTitle] = useState(null);
  const [editTitleValue, setEditTitleValue] = useState("");
  const [dashSessionFilter, setDashSessionFilter] = useState("");
  const [dashOptimizeCompare, setDashOptimizeCompare] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, onConfirm }
  const [attachedPdf, setAttachedPdf] = useState(null); // { file, s3Key, name, uploading, error }
  const pdfInputRef = useRef(null);
  const msgEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const menuRef = useRef(null);
  const modelMenuRef = useRef(null);
  const sessionMenuRef = useRef(null);

  // Persist theme
  useEffect(() => { localStorage.setItem("sa-theme", dark ? "dark" : "light"); }, [dark]);

  // Swap system prompt when RAG mode toggles (only if user hasn't customized)
  useEffect(() => {
    const oldDefault = ragEnabled ? DEFAULT_PROMPT : RAG_PROMPT;
    const newDefault = ragEnabled ? RAG_PROMPT : DEFAULT_PROMPT;
    // Only auto-swap if the current prompt is the old default (not custom)
    if (systemPrompt === oldDefault || systemPrompt === newDefault) {
      setSystemPrompt(newDefault);
      setEditingPrompt(newDefault);
      setPromptDirty(false);
      setOptimizedPrompts({});
    }
  }, [ragEnabled]);

  // Restore session on mount
  useEffect(() => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.getSession((err, session) => {
        if (!err && session && session.isValid()) {
          setToken(session.getIdToken().getJwtToken());
          const payload = session.getIdToken().decodePayload();
          setLoggedInEmail(payload.email || currentUser.getUsername());
          setMessages([GREETING]);
        }
        setSessionLoading(false);
      });
    } else {
      setSessionLoading(false);
    }
  }, []);

  // Auto-refresh token before expiry and on 401 errors
  const refreshAuthToken = useCallback(() => {
    return new Promise((resolve) => {
      const currentUser = userPool.getCurrentUser();
      if (!currentUser) { resolve(null); return; }
      currentUser.getSession((err, session) => {
        if (!err && session && session.isValid()) {
          const newToken = session.getIdToken().getJwtToken();
          setToken(newToken);
          resolve(newToken);
        } else {
          // Session expired and can't be refreshed — force re-login
          setToken(null);
          setLoggedInEmail("");
          setMessages([]);
          resolve(null);
        }
      });
    });
  }, []);

  // Refresh token every 45 minutes (Cognito tokens expire after 60 min)
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => { refreshAuthToken(); }, 45 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token, refreshAuthToken]);

  const scrollToBottom = useCallback(() => {
    msgEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);
  const prevMsgCount = useRef(messages.length);
  useEffect(() => {
    // Only auto-scroll when a new message is added or during active streaming — not on tab switches
    if (messages.length !== prevMsgCount.current || loading) { scrollToBottom(); }
    prevMsgCount.current = messages.length;
  }, [messages, loading, scrollToBottom]);

  // Fetch models when token is available
  useEffect(() => {
    if (!token) return;
    fetch(config.apiBase + "/models", {
      headers: { Authorization: token },
    })
      .then(r => r.json())
      .then(data => { setModelGroups(data); setModelsLoaded(true); })
      .catch(e => console.error("Failed to load models:", e));
  }, [token]);

  // Fetch sessions and load latest on login
  const fetchSessions = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(config.apiBase + "/sessions", { headers: { Authorization: token } });
      const data = await res.json();
      const fetched = data.sessions || [];
      // Merge with current state to preserve optimistic updates for sessions not yet in backend
      setSessions(prev => {
        const fetchedIds = new Set(fetched.map(s => s.sessionId));
        const optimistic = prev.filter(s => !fetchedIds.has(s.sessionId));
        return [...fetched, ...optimistic].sort((a, b) => (b.lastActivityAt || "").localeCompare(a.lastActivityAt || ""));
      });
      return fetched;
    } catch (e) { console.error("Failed to load sessions:", e); return []; }
  }, [token]);

  const loadSession = useCallback(async (sid) => {
    if (!token || !sid) return;
    try {
      const res = await fetch(config.apiBase + `/sessions/load?sessionId=${encodeURIComponent(sid)}`, { headers: { Authorization: token } });
      const data = await res.json();
      if (!data.messages || data.messages.length === 0) return;
      // Reconstruct chat messages from stored data
      const rebuilt = [GREETING];
      for (const msg of data.messages) {
        // User message
        rebuilt.push({ role: "user", text: msg.userPrompt, timestamp: msg.invocationTime, useRag: msg.useRag, autoOptimize: msg.isOptimized, useFewShot: msg.useFewShot });
        // Model responses
        const results = (msg.modelMetrics || []).map(mm => ({
          model: mm.modelId, kbKey: mm.kbKey || null, response: mm.responseText, error: false,
          metrics: { latencyMs: mm.latencyMs, ttftMs: mm.ttftMs, inputTokens: mm.inputTokens, outputTokens: mm.outputTokens, costUsd: mm.costUsd, cacheReadTokens: mm.cacheReadTokens, cacheWriteTokens: mm.cacheWriteTokens, cacheHitRate: mm.cacheHitRate, cacheSavingsUsd: mm.cacheSavingsUsd, pricingSource: mm.pricingSource, totalTokens: (mm.inputTokens || 0) + (mm.outputTokens || 0) + (mm.cacheReadTokens || 0) + (mm.cacheWriteTokens || 0) },
        }));
        const isRag = msg.useRag && results.some(r => r.kbKey);
        const kbKeys = isRag ? [...new Set(results.filter(r => r.kbKey).map(r => r.kbKey))] : [];
        rebuilt.push({ role: "assistant", results, activeTab: 0, timestamp: msg.invocationTime, useRag: isRag, kbKeys: isRag ? kbKeys : undefined, activeKbTab: {}, ragChunks: msg.ragChunks || undefined, ...(msg.perModelPrompts ? { perModelPrompts: msg.perModelPrompts, originalPrompt: msg.originalPrompt || "" } : {}),
          // Reconstruct originalResults for the original/optimized toggle
          ...(msg.originalModelMetrics ? { originalResults: msg.originalModelMetrics.map(om => ({
            model: om.modelId, kbKey: om.kbKey || null, response: om.responseText, error: false,
            metrics: { latencyMs: om.latencyMs, ttftMs: om.ttftMs, inputTokens: om.inputTokens, outputTokens: om.outputTokens, costUsd: om.costUsd, cacheReadTokens: om.cacheReadTokens, cacheWriteTokens: om.cacheWriteTokens, cacheHitRate: om.cacheHitRate, cacheSavingsUsd: om.cacheSavingsUsd, pricingSource: om.pricingSource, totalTokens: (om.inputTokens || 0) + (om.outputTokens || 0) + (om.cacheReadTokens || 0) + (om.cacheWriteTokens || 0) },
          })) } : {}),
        });
      }
      setMessages(rebuilt);
      setSessionId(sid);
      localStorage.setItem("sa-sessionId", sid);
    } catch (e) { console.error("Failed to load session:", e); }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      const allSessions = await fetchSessions();
      if (allSessions.length > 0) {
        const savedSid = localStorage.getItem("sa-sessionId");
        const targetSid = savedSid && allSessions.find(s => s.sessionId === savedSid) ? savedSid : allSessions[0].sessionId;
        await loadSession(targetSid);
      } else {
        // No sessions — create a new one
        const newSid = crypto.randomUUID();
        setSessionId(newSid);
        localStorage.setItem("sa-sessionId", newSid);
      }
    })();
  }, [token, fetchSessions, loadSession]);

  const handleNewChat = () => {
    const newSid = crypto.randomUUID();
    setSessionId(newSid);
    localStorage.setItem("sa-sessionId", newSid);
    setMessages([GREETING]);
    setAttachedPdf(null);
    fetchSessions();
  };

  const handleRenameSession = async (sid, newTitle) => {
    try {
      await fetch(config.apiBase + "/sessions", {
        method: "PUT", headers: { Authorization: token, "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId: sid, title: newTitle }),
      });
      setSessions(prev => prev.map(s => s.sessionId === sid ? { ...s, title: newTitle } : s));
      setEditingSessionTitle(null);
    } catch (e) { console.error("Failed to rename session:", e); }
  };

  const handleDeleteSession = async (sid) => {
    try {
      await fetch(config.apiBase + `/sessions?sessionId=${encodeURIComponent(sid)}`, {
        method: "DELETE", headers: { Authorization: token },
      });
      setSessions(prev => prev.filter(s => s.sessionId !== sid));
      // If we deleted the active session, start a new one
      if (sid === sessionId) {
        handleNewChat();
      }
    } catch (e) { console.error("Failed to delete session:", e); }
  };

  const handleSelectSession = async (sid) => {
    setShowSessionDropdown(false);
    setAttachedPdf(null);
    await loadSession(sid);
    fetchSessions();
  };

  const formatSessionTime = (isoStr) => {
    if (!isoStr) return "";
    const d = new Date(isoStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowUserMenu(false);
      if (modelMenuRef.current && !modelMenuRef.current.contains(e.target)) setShowModelMenu(false);
      if (sessionMenuRef.current && !sessionMenuRef.current.contains(e.target)) setShowSessionDropdown(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const t = dark ? darkTheme : lightTheme;

  const handleLogin = (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    const user = new CognitoUser({ Username: email, Pool: userPool });
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });
    user.authenticateUser(authDetails, {
      onSuccess: (result) => {
        setToken(result.getIdToken().getJwtToken());
        const payload = result.getIdToken().decodePayload();
        setLoggedInEmail(payload.email || email);
        setMessages([GREETING]);
        setLoginLoading(false);
      },
      onFailure: (err) => { setLoginError(err.message || "Login failed"); setLoginLoading(false); },
    });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;
    sendMessage(prompt.trim());
  };

  // ── PDF attachment handlers ──
  const MAX_PDF_SIZE = 4.5 * 1024 * 1024; // 4.5MB Bedrock limit

  const handlePdfSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset file input so the same file can be re-selected
    e.target.value = "";

    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setAttachedPdf({ error: "Only PDF files are supported" });
      setTimeout(() => setAttachedPdf(null), 3000);
      return;
    }
    if (file.size > MAX_PDF_SIZE) {
      setAttachedPdf({ error: `File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max 4.5MB.` });
      setTimeout(() => setAttachedPdf(null), 3000);
      return;
    }

    setAttachedPdf({ file, name: file.name, uploading: true, error: null, s3Key: null });

    try {
      // Get presigned URL
      const params = new URLSearchParams({ filename: file.name, sessionId: sessionId || "no-session" });
      const urlRes = await fetch(config.apiBase + `/chat/upload-url?${params}`, {
        headers: { Authorization: token },
      });
      const urlData = await urlRes.json();
      if (urlData.error) throw new Error(urlData.error);

      // Upload to S3
      await fetch(urlData.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/pdf" },
        body: file,
      });

      setAttachedPdf({ file, name: file.name, uploading: false, error: null, s3Key: urlData.key });
    } catch (err) {
      setAttachedPdf({ file, name: file.name, uploading: false, error: err.message, s3Key: null });
    }
  };

  const handleRemovePdf = () => {
    setAttachedPdf(null);
  };

  const handleLogout = () => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) currentUser.signOut();
    setToken(null); setMessages([]); setLoggedInEmail(""); setShowUserMenu(false);
    setSessions([]); setSessionId(null); localStorage.removeItem("sa-sessionId");
  };

  const selectedModelInfo = (() => {
    const primary = selectedModels[0] || "";
    for (const [prov, models] of Object.entries(modelGroups)) {
      const found = models.find(m => m.modelId === primary);
      if (found) return { ...found, icon: PROVIDER_ICONS[prov] || "⬜" };
    }
    return { modelName: primary.split(".").pop(), providerName: "Unknown", icon: "🤖" };
  })();

  const getModelName = (modelId) => {
    for (const [prov, models] of Object.entries(modelGroups)) {
      const found = models.find(m => m.modelId === modelId);
      if (found) return found.modelName;
    }
    return modelId.split(".").pop();
  };

  const [copiedKey, setCopiedKey] = useState(null);
  const copyText = (text, key) => { navigator.clipboard.writeText(text).then(() => { setCopiedKey(key); setTimeout(() => setCopiedKey(null), 1500); }); };
  const CopyBtn = ({ text, copyKey, label }) => (
    <button onClick={(e) => { e.stopPropagation(); copyText(text, copyKey); }}
      style={{ display: "inline-flex", alignItems: "center", gap: "3px", padding: "3px 8px", borderRadius: "6px", border: `1px solid ${t.border}`, backgroundColor: "transparent", color: copiedKey === copyKey ? "#22c55e" : t.muted, cursor: "pointer", fontSize: "10px", fontWeight: 600, transition: "all 0.2s" }}>
      {copiedKey === copyKey ? "✓ Copied" : (label || "📋 Copy")}
    </button>
  );

  const toggleModel = (modelId) => {
    setSelectedModels(prev => {
      if (prev.includes(modelId)) {
        return prev.length > 1 ? prev.filter(id => id !== modelId) : prev;
      }
      return prev.length >= MAX_MODELS ? prev : [...prev, modelId];
    });
    setOptimizedPrompts({});  // Clear cached optimized prompts when models change
  };

  const sendMessage = useCallback(async (userMsg) => {
    if (!userMsg.trim() || loading) return;
    const isRagMode = ragEnabled;
    // Capture attached PDF before clearing
    // Capture attached PDF — persist across messages in the session (user can remove manually)
    const currentPdf = attachedPdf && attachedPdf.s3Key ? { s3Key: attachedPdf.s3Key, name: attachedPdf.name, format: "pdf" } : null;
    setMessages((prev) => [...prev, { role: "user", text: userMsg, timestamp: new Date().toISOString(), useRag: isRagMode, useFewShot, autoOptimize, ...(currentPdf ? { attachedPdf: currentPdf.name } : {}) }]);
    // Update session title optimistically on first message
    setSessions(prev => {
      const existing = prev.find(s2 => s2.sessionId === sessionId);
      if (existing) {
        if (!existing.title || existing.title === existing.firstPrompt) {
          return prev.map(s2 => s2.sessionId === sessionId ? { ...s2, title: userMsg.substring(0, 200), firstPrompt: userMsg.substring(0, 200) } : s2);
        }
        return prev;
      }
      return [{ sessionId, title: userMsg.substring(0, 200), firstPrompt: userMsg.substring(0, 200), createdAt: new Date().toISOString(), lastActivityAt: new Date().toISOString(), messageCount: 1 }, ...prev];
    });
    setPrompt("");
    setLoading(true);
    try {
      const cleanHistory = messages.filter(m => m !== GREETING).reduce((acc, m) => {
        if (m.role === "user") acc.push({ role: "user", text: m.text });
        else if (m.results && m.results[0]) acc.push({ role: "assistant", text: m.results[0].response });
        return acc;
      }, []);

      const basePrompt = useFewShot && fewShotText ? systemPrompt + "\n\n--- FEW-SHOT EXAMPLES ---\n" + fewShotText : systemPrompt;

      // Auto-optimize: get per-model optimized prompts (reuse cached if prompt + models haven't changed)
      let perModelPrompts = {};
      if (autoOptimize) {
        const cachedValid = optimizedPrompts && Object.keys(optimizedPrompts).length > 0
          && selectedModels.every(mid => optimizedPrompts[mid]);
        if (cachedValid) {
          perModelPrompts = optimizedPrompts;
          console.log("Reusing cached optimized prompts");
        } else {
          try {
            setOptimizing(true);
            const optRes = await fetch(config.apiBase + "/optimize", {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: token },
              body: JSON.stringify({ systemPrompt: basePrompt, targetModelIds: selectedModels }),
            });
            if (optRes.ok) {
              const optData = await optRes.json();
              for (const [mid, val] of Object.entries(optData.results || {})) {
                if (val.optimizedPrompt) perModelPrompts[mid] = val.optimizedPrompt;
              }
              setOptimizedPrompts(perModelPrompts);
            }
          } catch (e) { console.error("Auto-optimize failed, using original prompt:", e); }
          finally { setOptimizing(false); }
        }
      }

      const res = await fetch(config.chatStreamUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ prompt: userMsg, history: cleanHistory, modelIds: selectedModels, systemPrompt: basePrompt, perModelPrompts, judgeModel, useRag: isRagMode, useFewShot: !!(useFewShot && fewShotText), sessionId, ...(currentPdf ? { document: currentPdf } : {}) }),
      });

      if (!res.ok) {
        // Try token refresh on 401
        if (res.status === 401) {
          const newToken = await refreshAuthToken();
          if (newToken) {
            setMessages((prev) => [...prev, { role: "error", text: "Session refreshed — please resend your message." }]);
          } else {
            setMessages((prev) => [...prev, { role: "error", text: "Session expired. Please sign in again." }]);
          }
          setLoading(false);
          return;
        }
        const err = await res.json().catch(() => ({ error: "Request failed" }));
        setMessages((prev) => [...prev, { role: "error", text: err.error || "Error" }]);
        setLoading(false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      const modelTexts = {};
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === "start") {
              if (event.useRag && event.kbKeys && event.kbKeys.length > 0) {
                // RAG mode: create placeholders for each model × KB combo
                const placeholders = [];
                for (const mid of event.models) {
                  for (const kbKey of event.kbKeys) {
                    placeholders.push({ model: mid, kbKey, response: "", error: false, metrics: {} });
                  }
                }
                setMessages((prev) => [...prev, {
                  role: "assistant", results: placeholders, activeTab: 0,
                  useRag: true, kbConfigs: event.kbConfigs, kbKeys: event.kbKeys,
                  activeKbTab: {},
                  ...(Object.keys(perModelPrompts).length > 0 ? { perModelPrompts, originalPrompt: basePrompt } : {}),
                }]);
              } else {
                const placeholders = event.models.map(mid => ({ model: mid, response: "", error: false, metrics: {} }));
                setMessages((prev) => [...prev, {
                  role: "assistant", results: placeholders, activeTab: 0,
                  ...(Object.keys(perModelPrompts).length > 0 ? { perModelPrompts, originalPrompt: basePrompt } : {}),
                }]);
              }
            } else if (event.type === "delta") {
              const textKey = event.kbKey ? `${event.model}::${event.kbKey}` : event.model;
              modelTexts[textKey] = (modelTexts[textKey] || "") + event.text;
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.results) {
                  updated[updated.length - 1] = { ...last, results: last.results.map(r => {
                    const rKey = r.kbKey ? `${r.model}::${r.kbKey}` : r.model;
                    return rKey === textKey ? { ...r, response: modelTexts[textKey] } : r;
                  })};
                }
                return updated;
              });
            } else if (event.type === "done") {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.results && event.results) {
                  const newLast = { ...last, results: last.results.map(r => {
                    const final = event.results.find(f =>
                      f.model === r.model && (f.kbKey || null) === (r.kbKey || null)
                    );
                    return final ? { ...r, metrics: final.metrics, error: final.error } : r;
                  })};
                  // Store original results when auto-optimize is active
                  if (event.originalResults) {
                    newLast.originalResults = event.originalResults;
                  }
                  updated[updated.length - 1] = newLast;
                }
                return updated;
              });
            } else if (event.type === "rag-chunks") {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.useRag) {
                  updated[updated.length - 1] = { ...last, ragChunks: event.chunks };
                }
                return updated;
              });
            } else if (event.type === "error") {
              setMessages((prev) => [...prev, { role: "error", text: event.error }]);
            }
          } catch (_) {}
        }
      }
    } catch (err) {
      setMessages((prev) => [...prev, { role: "error", text: err.message }]);
    } finally { setLoading(false); fetchSessions(); setTimeout(() => chatInputRef.current?.focus(), 100); }
  }, [loading, messages, token, selectedModels, systemPrompt, judgeModel, ragEnabled, sessionId, useFewShot, fewShotText, autoOptimize, attachedPdf]);

  const handleSuggestion = (text) => {
    setPrompt(text);
    const fakeEvent = { preventDefault: () => {} };
    setTimeout(() => {
      const form = document.querySelector("form");
      if (form) form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
    }, 50);
  };

  if (sessionLoading) {
    return (
      <div style={{ ...s.fullPage, backgroundColor: t.bg, color: t.muted, justifyContent: "center", alignItems: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ color: t.accent, marginBottom: "12px" }}><BotIcon /></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div style={{ ...s.fullPage, backgroundColor: t.bg, color: t.text }}>
        {/* Theme toggle on login */}
        <div style={{ position: "absolute", top: "20px", right: "24px" }}>
          <button onClick={() => setDark(!dark)}
            style={{ ...s.themeToggle, color: t.muted, borderColor: t.border, backgroundColor: t.card }}
            aria-label="Toggle theme">{dark ? "☀️ Light" : "🌙 Dark"}</button>
        </div>

        <div style={{ ...s.loginCard, backgroundColor: t.card, boxShadow: t.shadow }}>
          {/* Logo area */}
          <div style={{ textAlign: "center", marginBottom: "32px" }}>
            <div style={{
              width: "64px", height: "64px", borderRadius: "16px", margin: "0 auto 16px",
              background: `linear-gradient(135deg, ${t.accent}, ${t.accentAlt})`,
              display: "flex", alignItems: "center", justifyContent: "center", color: "#fff"
            }}>
              <BotIcon />
            </div>
            <h1 style={{ margin: "0 0 4px", fontSize: "24px", fontWeight: 700, color: t.text }}>SA Enablement</h1>
            <p style={{ margin: 0, color: t.muted, fontSize: "14px" }}>GenAI Chat · Powered by Amazon Bedrock</p>
          </div>

          {/* Divider */}
          <div style={{ height: "1px", backgroundColor: t.border, margin: "0 0 24px" }} />

          <form onSubmit={handleLogin}>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: t.muted, marginBottom: "6px" }}>Email</label>
            <div style={{ position: "relative", marginBottom: "16px" }}>
              <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", fontSize: "15px", opacity: 0.5 }}>📧</span>
              <input type="email" placeholder="you@example.com" value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ ...s.loginInput, backgroundColor: t.inputBg, color: t.text, borderColor: t.border, paddingLeft: "38px" }}
                required aria-label="Email address" />
            </div>

            <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: t.muted, marginBottom: "6px" }}>Password</label>
            <div style={{ position: "relative", marginBottom: "20px" }}>
              <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", fontSize: "15px", opacity: 0.5 }}>🔒</span>
              <input type="password" placeholder="Enter your password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ ...s.loginInput, backgroundColor: t.inputBg, color: t.text, borderColor: t.border, paddingLeft: "38px" }}
                required aria-label="Password" />
            </div>

            {loginError && (
              <div style={{ backgroundColor: dark ? "#3b1111" : "#fef2f2", border: `1px solid ${dark ? "#7f1d1d" : "#fecaca"}`, borderRadius: "10px", padding: "10px 14px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                <span>⚠️</span>
                <span style={{ color: dark ? "#fca5a5" : "#dc2626", fontSize: "13px" }}>{loginError}</span>
              </div>
            )}

            <button type="submit" disabled={loginLoading}
              style={{ ...s.loginBtn, background: `linear-gradient(135deg, ${t.accent}, ${t.accentAlt})`, opacity: loginLoading ? 0.7 : 1 }}>
              {loginLoading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          {/* Footer */}
          <div style={{ textAlign: "center", marginTop: "24px" }}>
            <div style={{ display: "flex", justifyContent: "center", gap: "16px", flexWrap: "wrap" }}>
              <span style={{ fontSize: "11px", color: t.muted }}>🟠 Amazon Nova</span>
              <span style={{ fontSize: "11px", color: t.muted }}>🟣 Anthropic Claude</span>
              <span style={{ fontSize: "11px", color: t.muted }}>🔵 Meta Llama</span>
              <span style={{ fontSize: "11px", color: t.muted }}>🟡 Mistral AI</span>
            </div>
            <p style={{ fontSize: "11px", color: t.muted, marginTop: "12px", opacity: 0.6 }}>Secured with Amazon Cognito</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...s.fullPage, backgroundColor: t.bg, color: t.text, flexDirection: "row" }}>
      {/* Main area */}
      <div onClick={() => { if (showSidebar) setShowSidebar(false); }} style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ ...s.topBar, backgroundColor: t.card, borderBottomColor: t.border }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ color: t.accent }}><BotIcon /></div>
            <span style={{ fontWeight: 700, fontSize: "16px", color: t.accent }}>SA Enablement</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {/* Model summary badge */}
            <div ref={modelMenuRef} style={{ position: "relative" }}>
              <button onClick={() => setShowModelMenu(!showModelMenu)} className="header-btn"
                style={{ ...s.iconBtn, color: t.muted, borderColor: t.border, fontSize: "12px", display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: t.accent, flexShrink: 0 }} />
                <span>{selectedModels.length} model{selectedModels.length !== 1 ? "s" : ""}</span>
                <span style={{ fontSize: "9px", opacity: 0.5 }}>▼</span>
              </button>
              {showModelMenu && (
                <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", minWidth: "240px", backgroundColor: dark ? "#120e28" : "#fff", border: `1px solid ${dark ? "rgba(232,101,26,0.2)" : t.border}`, borderRadius: "12px", boxShadow: dark ? "0 8px 24px rgba(0,0,0,0.5)" : "0 8px 24px rgba(0,0,0,0.12)", zIndex: 100, overflow: "hidden" }}>
                  <div style={{ padding: "10px 14px", borderBottom: `1px solid ${t.border}`, fontSize: "11px", fontWeight: 700, color: t.accent }}>Selected Models</div>
                  {selectedModels.map((mid, idx) => {
                    let provIcon = "⬜";
                    for (const [prov, models] of Object.entries(modelGroups)) {
                      if (models.find(m => m.modelId === mid)) { provIcon = PROVIDER_ICONS[prov] || "⬜"; break; }
                    }
                    return (
                      <div key={mid} style={{ padding: "8px 14px", display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: t.text, borderBottom: idx < selectedModels.length - 1 ? `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}` : "none" }}>
                        <span>{provIcon}</span>
                        <span style={{ fontWeight: 500 }}>{getModelName(mid)}</span>
                        <span style={{ marginLeft: "auto", fontSize: "10px", color: t.muted, opacity: 0.6 }}>{mid.split(".").slice(0, 2).join(".")}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {/* Session switcher — only on chat tab */}
            {currentTab === "chat" && (
              <div ref={sessionMenuRef} style={{ position: "relative" }}>
                <div style={{ display: "flex", gap: "4px" }}>
                  <button onClick={() => setShowSessionDropdown(!showSessionDropdown)}
                    className="header-btn"
                    style={{ ...s.iconBtn, color: t.muted, borderColor: t.border, maxWidth: "220px", fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}
                    title={sessions.find(s2 => s2.sessionId === sessionId)?.title || "Current session"}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>📋 {sessions.find(s2 => s2.sessionId === sessionId)?.title?.substring(0, 25) || "New Session"}{sessions.find(s2 => s2.sessionId === sessionId)?.title?.length > 25 ? "..." : ""}</span>
                    <span style={{ fontSize: "9px", opacity: 0.5, flexShrink: 0 }}>▼</span>
                  </button>
                  <button onClick={handleNewChat} className="header-btn" style={{ ...s.iconBtn, color: t.accent, borderColor: t.accent + "44", fontWeight: 700, fontSize: "14px", padding: "6px 10px" }} title="New chat">+</button>
                </div>
                {showSessionDropdown && (
                  <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", width: "340px", backgroundColor: dark ? "#120e28" : "#fff", border: `1px solid ${dark ? "rgba(232,101,26,0.2)" : t.border}`, borderRadius: "12px", boxShadow: dark ? "0 8px 24px rgba(0,0,0,0.5)" : "0 8px 24px rgba(0,0,0,0.12)", zIndex: 100, overflow: "hidden" }}>
                    <div style={{ padding: "10px 12px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontSize: "12px", fontWeight: 700, color: t.accent }}>Chat Sessions</span>
                      <span style={{ fontSize: "10px", color: t.muted }}>{sessions.length} session{sessions.length !== 1 ? "s" : ""}</span>
                    </div>
                    <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                      {sessions.length === 0 ? (
                        <div style={{ padding: "16px", textAlign: "center", fontSize: "12px", color: t.muted }}>No sessions yet</div>
                      ) : sessions.map((sess) => (
                        <div key={sess.sessionId}
                          style={{ padding: "8px 12px", borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}`, cursor: "pointer", backgroundColor: sess.sessionId === sessionId ? (dark ? "rgba(232,101,26,0.1)" : "rgba(9,114,211,0.06)") : "transparent", transition: "background 0.15s" }}
                          onClick={() => handleSelectSession(sess.sessionId)}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            {editingSessionTitle === sess.sessionId ? (
                              <input autoFocus value={editTitleValue}
                                onChange={(e) => setEditTitleValue(e.target.value)}
                                onBlur={() => handleRenameSession(sess.sessionId, editTitleValue)}
                                onKeyDown={(e) => { if (e.key === "Enter") handleRenameSession(sess.sessionId, editTitleValue); if (e.key === "Escape") setEditingSessionTitle(null); }}
                                onClick={(e) => e.stopPropagation()}
                                style={{ flex: 1, fontSize: "12px", padding: "2px 6px", backgroundColor: t.inputBg, color: t.text, border: `1px solid ${t.accent}`, borderRadius: "4px", outline: "none" }} />
                            ) : (
                              <span style={{ flex: 1, fontSize: "12px", fontWeight: sess.sessionId === sessionId ? 700 : 400, color: sess.sessionId === sessionId ? t.accent : t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {sess.title || sess.firstPrompt || "Untitled"}
                              </span>
                            )}
                            <button onClick={(e) => { e.stopPropagation(); setEditingSessionTitle(sess.sessionId); setEditTitleValue(sess.title || sess.firstPrompt || ""); }}
                              style={{ background: "none", border: "none", cursor: "pointer", color: t.muted, fontSize: "11px", padding: "2px", opacity: 0.5 }} title="Rename">✏️</button>
                            <button onClick={(e) => { e.stopPropagation(); if (window.confirm("Delete this session?")) handleDeleteSession(sess.sessionId); }}
                              style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: "11px", padding: "2px", opacity: 0.5 }} title="Delete">🗑️</button>
                          </div>
                          <div style={{ display: "flex", gap: "8px", marginTop: "2px", fontSize: "10px", color: t.muted }}>
                            <span>{formatSessionTime(sess.lastActivityAt)}</span>
                            <span>{sess.messageCount} msg{sess.messageCount !== 1 ? "s" : ""}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <button onClick={() => setDark(!dark)} className="header-btn" style={{ ...s.iconBtn, color: t.muted, borderColor: t.border }} aria-label="Toggle theme">{dark ? "☀️" : "🌙"}</button>
            <div ref={menuRef} style={{ position: "relative" }}>
              <button onClick={() => setShowUserMenu(!showUserMenu)}
                className="header-btn"
                style={{ ...s.userBtn, backgroundColor: t.accent + "22", color: t.accent, borderColor: t.accent + "44" }}>
                <span style={{ width: "28px", height: "28px", borderRadius: "50%", backgroundColor: t.accent, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: 700 }}>
                  {loggedInEmail.charAt(0).toUpperCase()}
                </span>
                <span style={{ fontSize: "13px", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{loggedInEmail}</span>
                <span style={{ fontSize: "10px" }}>▼</span>
              </button>
              {showUserMenu && (
                <div style={{ ...s.dropdown, backgroundColor: t.card, borderColor: t.border, boxShadow: t.shadow }}>
                  <div style={{ padding: "10px 14px", borderBottom: `1px solid ${t.border}`, fontSize: "12px", color: t.muted }}>
                    Signed in as
                    <div style={{ fontWeight: 600, color: t.text, marginTop: "2px", fontSize: "13px" }}>{loggedInEmail}</div>
                  </div>
                  <button onClick={handleLogout} style={{ ...s.dropdownItem, color: "#ef4444" }}>🚪 Sign Out</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab Bar */}
        <div style={{ display: "flex", borderBottom: `1px solid ${t.border}`, backgroundColor: t.card, flexShrink: 0 }}>
          {[{key: "chat", label: "💬 Chat"}, {key: "metrics", label: "📊 Model Eval Metrics"}].map(tab => (
            <button key={tab.key} className={`tab-btn${currentTab === tab.key ? " active" : ""}`}
              onClick={() => setCurrentTab(tab.key)}
              style={{ padding: "10px 20px", border: "none", borderBottom: currentTab === tab.key ? `2px solid ${t.accent}` : "2px solid transparent", backgroundColor: "transparent", color: currentTab === tab.key ? t.accent : t.muted, cursor: "pointer", fontSize: "13px", fontWeight: currentTab === tab.key ? 700 : 400, transition: "all 0.15s" }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {currentTab === "metrics" ? (
          <Dashboard token={token} dark={dark} range={dashRange} customStart={dashCustomStart} customEnd={dashCustomEnd} refreshKey={dashRefreshKey} selectedMetrics={dashMetrics} modelGroups={modelGroups} modelFilter={dashModelFilter} sessionFilter={dashSessionFilter} sessions={sessions} optimizeCompare={dashOptimizeCompare} onOptimizedCount={setDashHasOptimized} />
        ) : (<>

        {/* Messages */}
        <div style={s.messagesWrap}>
        {messages.map((m, i) => {
          // Greeting or plain assistant message (no results array)
          if (m.role === "assistant" && !m.results) {
            return (
              <div key={i} style={{ display: "flex", gap: "10px", padding: "4px 0", alignItems: "flex-start" }}>
                <div style={{ width: "32px", height: "32px", borderRadius: "50%", backgroundColor: t.accent, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "4px" }}>
                  <SparkleIcon />
                </div>
                <div style={{ padding: "12px 16px", borderRadius: "16px", borderTopLeftRadius: "4px", fontSize: "14px", lineHeight: 1.5, whiteSpace: "normal", wordBreak: "break-word", backgroundColor: t.assistantBg, color: t.text, maxWidth: "70%", border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}` }}>
                  <div className="md-content"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown></div>
                </div>
              </div>
            );
          }

          // Multi-model tabbed response
          if (m.results) {
            // In RAG mode, primary tabs = unique models, secondary tabs = KB configs
            // In normal mode, primary tabs = results (one per model)
            const isRag = m.useRag && m.kbKeys && m.kbKeys.length > 0;
            const uniqueModels = isRag
              ? [...new Set(m.results.map(r => r.model))]
              : m.results.map(r => r.model);
            const activeModelIdx = m.activeTab || 0;
            const activeModelId = uniqueModels[activeModelIdx] || uniqueModels[0];

            // For RAG: get results for the active model, one per KB
            const activeKbTabKey = `kb_${activeModelId}`;
            const activeKbIdx = (m.activeKbTab && m.activeKbTab[activeModelId]) || 0;

            let active;
            if (isRag) {
              const modelResults = m.results.filter(r => r.model === activeModelId);
              active = modelResults[activeKbIdx] || modelResults[0];
            } else {
              active = m.results[activeModelIdx] || m.results[0];
            }

            // Determine if we should show original response (toggle)
            const hasOriginal = m.originalResults && m.originalResults.length > 0 && m.perModelPrompts;
            const showOriginalKey = `showOriginal_${activeModelId}${isRag ? `_${(active && active.kbKey) || ''}` : ''}`;
            const isShowingOriginal = m[showOriginalKey] || false;

            // If showing original, swap active to the original result
            let displayActive = active;
            if (isShowingOriginal && hasOriginal) {
              const origMatch = m.originalResults.find(r =>
                r.model === (active && active.model) && (r.kbKey || null) === ((active && active.kbKey) || null)
              );
              if (origMatch) {
                displayActive = { ...active, response: origMatch.response, metrics: origMatch.metrics, error: origMatch.error };
              }
            }

            const showCompare = uniqueModels.length > 1 && m.results.some(r => r.metrics?.latencyMs);
            const showChunksTab = isRag && m.ragChunks;
            const showOptTab = m.perModelPrompts && Object.keys(m.perModelPrompts).length > 0;
            const optTabIdx = uniqueModels.length + (showCompare ? 1 : 0) + (showChunksTab ? 1 : 0);

            return (
              <div key={i} style={{ display: "flex", gap: "10px", padding: "4px 0", alignItems: "flex-start" }}>
                <div style={{ width: "32px", height: "32px", borderRadius: "50%", backgroundColor: t.accent, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "4px" }}>
                  <SparkleIcon />
                </div>
                <div className="response-bubble" style={{ flex: 1, maxWidth: "85%", borderRadius: "16px", borderTopLeftRadius: "4px", overflow: "hidden", backgroundColor: t.assistantBg, border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}` }}>
                  {/* Primary tabs — Models */}
                  <div style={{ display: "flex", overflowX: "auto", borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`, backgroundColor: dark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.02)" }}>
                    {uniqueModels.map((mid, ri) => (
                      <button key={ri} className={`tab-btn${ri === activeModelIdx ? " active" : ""}`} onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? {...msg, activeTab: ri} : msg))}
                        style={{ padding: "8px 14px", border: "none", borderBottom: ri === activeModelIdx ? `2px solid ${t.accent}` : "2px solid transparent", backgroundColor: "transparent", color: ri === activeModelIdx ? t.accent : t.muted, cursor: "pointer", fontSize: "12px", fontWeight: ri === activeModelIdx ? 700 : 400, whiteSpace: "nowrap", transition: "all 0.15s" }}>
                        💬 {getModelName(mid)}
                      </button>
                    ))}
                    {showCompare && (
                      <button className={`tab-btn${activeModelIdx === uniqueModels.length ? " active" : ""}`} onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? {...msg, activeTab: uniqueModels.length} : msg))}
                        style={{ padding: "8px 14px", border: "none", borderBottom: activeModelIdx === uniqueModels.length ? `2px solid ${dark ? "#00d4ff" : "#6366f1"}` : `1px solid ${dark ? "rgba(0,212,255,0.2)" : "rgba(99,102,241,0.2)"}`, backgroundColor: activeModelIdx === uniqueModels.length ? (dark ? "rgba(0,212,255,0.08)" : "rgba(99,102,241,0.06)") : "transparent", color: activeModelIdx === uniqueModels.length ? (dark ? "#00d4ff" : "#6366f1") : (dark ? "rgba(0,212,255,0.5)" : "rgba(99,102,241,0.5)"), cursor: "pointer", fontSize: "11px", fontWeight: activeModelIdx === uniqueModels.length ? 700 : 500, whiteSpace: "nowrap", transition: "all 0.15s" }}>
                        📊 Compare Stats
                      </button>
                    )}
                    {showChunksTab && (
                      <button className={`tab-btn${activeModelIdx === uniqueModels.length + (showCompare ? 1 : 0) ? " active" : ""}`}
                        onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? {...msg, activeTab: uniqueModels.length + (showCompare ? 1 : 0)} : msg))}
                        style={{ padding: "8px 14px", border: "none", borderBottom: activeModelIdx === uniqueModels.length + (showCompare ? 1 : 0) ? `2px solid ${dark ? "#00d4ff" : "#6366f1"}` : `1px solid ${dark ? "rgba(0,212,255,0.2)" : "rgba(99,102,241,0.2)"}`, backgroundColor: activeModelIdx === uniqueModels.length + (showCompare ? 1 : 0) ? (dark ? "rgba(0,212,255,0.08)" : "rgba(99,102,241,0.06)") : "transparent", color: activeModelIdx === uniqueModels.length + (showCompare ? 1 : 0) ? (dark ? "#00d4ff" : "#6366f1") : (dark ? "rgba(0,212,255,0.5)" : "rgba(99,102,241,0.5)"), cursor: "pointer", fontSize: "11px", fontWeight: activeModelIdx === uniqueModels.length + (showCompare ? 1 : 0) ? 700 : 500, whiteSpace: "nowrap", transition: "all 0.15s" }}>
                        📄 Retrieved Chunks
                      </button>
                    )}
                    {showOptTab && (
                      <button className={`tab-btn${activeModelIdx === optTabIdx ? " active" : ""}`}
                        onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? {...msg, activeTab: optTabIdx} : msg))}
                        style={{ padding: "8px 14px", border: "none", borderBottom: activeModelIdx === optTabIdx ? `2px solid ${dark ? "#22c55e" : "#16a34a"}` : `1px solid ${dark ? "rgba(34,197,94,0.2)" : "rgba(22,163,74,0.2)"}`, backgroundColor: activeModelIdx === optTabIdx ? (dark ? "rgba(34,197,94,0.08)" : "rgba(22,163,74,0.06)") : "transparent", color: activeModelIdx === optTabIdx ? (dark ? "#22c55e" : "#16a34a") : (dark ? "rgba(34,197,94,0.5)" : "rgba(22,163,74,0.5)"), cursor: "pointer", fontSize: "11px", fontWeight: activeModelIdx === optTabIdx ? 700 : 500, whiteSpace: "nowrap", transition: "all 0.15s" }}>
                        ✨ Optimized Prompts
                      </button>
                    )}
                  </div>

                  {/* Secondary tabs — KB configs (RAG mode only) */}
                  {isRag && activeModelIdx < uniqueModels.length && (() => {
                    const modelResults = m.results.filter(r => r.model === activeModelId);
                    return (
                      <div style={{ display: "flex", overflowX: "auto", gap: "6px", padding: "6px 12px", borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"}`, backgroundColor: dark ? "rgba(0,0,0,0.1)" : "rgba(0,0,0,0.01)" }}>
                        {modelResults.map((r, ki) => {
                          const kbLabel = (r.kbKey || "")
                            .replace("default-", "Default · ").replace("semantic-", "Semantic · ")
                            .replace("titan", "Titan").replace("cohere", "Cohere");
                          return (
                            <button key={ki}
                              onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? {...msg, activeKbTab: {...(msg.activeKbTab || {}), [activeModelId]: ki}} : msg))}
                              style={{
                                padding: "4px 10px", borderRadius: "12px", border: `1px solid ${ki === activeKbIdx ? t.accent : t.border}`,
                                backgroundColor: ki === activeKbIdx ? (dark ? "rgba(232,101,26,0.15)" : "rgba(9,114,211,0.08)") : "transparent",
                                color: ki === activeKbIdx ? t.accent : t.muted, cursor: "pointer", fontSize: "10px", fontWeight: ki === activeKbIdx ? 700 : 400,
                                whiteSpace: "nowrap", transition: "all 0.15s",
                              }}>
                              🔍 {kbLabel}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Active tab content */}
                  {(isRag ? activeModelIdx < uniqueModels.length : activeModelIdx < m.results.length) ? (
                  <div style={{ padding: "12px 16px", fontSize: "14px", lineHeight: 1.5, color: t.text }}>
                    {/* Original / Optimized toggle */}
                    {hasOriginal && activeModelIdx < uniqueModels.length && (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px", padding: "6px 10px", borderRadius: "8px", backgroundColor: dark ? "rgba(34,197,94,0.06)" : "rgba(34,197,94,0.04)", border: `1px solid ${dark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.1)"}` }}>
                        <span style={{ fontSize: "10px", fontWeight: 600, color: isShowingOriginal ? t.muted : "#22c55e", letterSpacing: "0.02em" }}>
                          {isShowingOriginal ? "📄 Response based on Original Prompt" : "✨ Response based on Optimized Prompt"}
                        </span>
                        <div
                          onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? { ...msg, [showOriginalKey]: !isShowingOriginal } : msg))}
                          style={{ width: "32px", height: "16px", borderRadius: "8px", position: "relative", backgroundColor: isShowingOriginal ? (dark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)") : "#22c55e", cursor: "pointer", transition: "background-color 0.2s", flexShrink: 0 }}>
                          <div style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: "#fff", position: "absolute", top: "2px", left: isShowingOriginal ? "2px" : "18px", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                        </div>
                        <span style={{ fontSize: "9px", color: t.muted, fontStyle: "italic" }}>
                          {isShowingOriginal ? "Switch to optimized →" : "← Switch to original"}
                        </span>
                        <button onClick={() => {
                          const targetMid = active.model || activeModelId;
                          setMessages(prev => prev.map((msg, mi) => {
                            if (mi !== i) return msg;
                            const updates = { ...msg, activeTab: optTabIdx };
                            for (const uid of uniqueModels) {
                              updates[`opt_${i}_${uid}`] = uid === targetMid;
                            }
                            return updates;
                          }));
                        }}
                          style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: "4px", padding: "3px 8px", borderRadius: "6px", border: "1px solid rgba(34,197,94,0.3)", backgroundColor: "transparent", color: "#22c55e", cursor: "pointer", fontSize: "9px", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>
                          ✨ View optimized system prompt →
                        </button>
                        {isRag && active.kbKey && showChunksTab && (
                        <button onClick={() => {
                          const chunksTabIdx = uniqueModels.length + (showCompare ? 1 : 0);
                          setMessages(prev => prev.map((msg, mi) => mi === i ? {
                            ...msg, activeTab: chunksTabIdx, [`chunks_${active.kbKey}`]: true,
                          } : msg));
                        }}
                          style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "3px 8px", borderRadius: "6px", border: `1px solid ${t.accent}33`, backgroundColor: "transparent", color: t.accent, cursor: "pointer", fontSize: "9px", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>
                          📄 View retrieved chunks →
                        </button>
                        )}
                      </div>
                    )}
                    {displayActive.error ? (
                      <span style={{ color: "#ef4444" }}>{displayActive.response}</span>
                    ) : (
                      <div className="md-content"><ReactMarkdown remarkPlugins={[remarkGfm]}>{displayActive.response}</ReactMarkdown></div>
                    )}
                    {/* View retrieved chunks link — only when auto-optimize is OFF */}
                    {!hasOriginal && isRag && active.kbKey && showChunksTab && (
                      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "4px" }}>
                        <button onClick={() => {
                          const chunksTabIdx = uniqueModels.length + (showCompare ? 1 : 0);
                          setMessages(prev => prev.map((msg, mi) => mi === i ? {
                            ...msg, activeTab: chunksTabIdx, [`chunks_${active.kbKey}`]: true,
                          } : msg));
                        }}
                          style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "3px 8px", borderRadius: "6px", border: `1px solid ${t.accent}33`, backgroundColor: "transparent", color: t.accent, cursor: "pointer", fontSize: "10px", fontWeight: 600 }}>
                          📄 View retrieved chunks →
                        </button>
                      </div>
                    )}
                    {/* View optimized prompt link */}
                    {displayActive.metrics && displayActive.metrics.latencyMs !== undefined && (
                      <div style={{ marginTop: "10px", padding: "10px 12px", borderRadius: "10px", backgroundColor: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)", border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`, fontSize: "11.5px", fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace" }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "14px", color: dark ? "#c4c4c4" : "#4b5563" }}>
                          <span title="TTFT (Time To First Token): Time from sending the request to receiving the first token back from the model." style={{ fontWeight: 600 }}>⚡ TTFT <span style={{ color: t.accent }}>{displayActive.metrics.ttftMs}ms</span></span>
                          <span style={{ opacity: 0.3 }}>|</span>
                          <span title="Latency: Total round-trip time from sending the request to receiving the complete response." style={{ fontWeight: 600 }}>⏱ Latency <span style={{ color: t.accent }}>{displayActive.metrics.latencyMs}ms</span></span>
                          <span style={{ opacity: 0.3 }}>|</span>
                          <span title="Input Tokens: Non-cached input tokens (excludes cache read/write). Charged at standard input rate." style={{ fontWeight: 600 }}>🔼 Input Tokens <span style={{ color: dark ? "#00d4ff" : "#2563eb" }}>{displayActive.metrics.inputTokens}</span></span>
                          <span style={{ opacity: 0.3 }}>|</span>
                          <span title="Output Tokens: Number of tokens generated by the model in its response." style={{ fontWeight: 600 }}>🔽 Output Tokens <span style={{ color: dark ? "#00d4ff" : "#2563eb" }}>{displayActive.metrics.outputTokens}</span></span>
                          <span style={{ opacity: 0.3 }}>|</span>
                          <span title={"Cost = (inputTokens × inputRate) + (cacheRead × readRate) + (cacheWrite × writeRate) + (outputTokens × outputRate). Per 1K tokens."} style={{ fontWeight: 700 }}>💰 Cost <span style={{ color: dark ? "#ffe156" : "#d97706" }}>${displayActive.metrics.costUsd < 0.001 ? displayActive.metrics.costUsd.toFixed(6) : displayActive.metrics.costUsd.toFixed(4)}</span></span>
                          <span title="Pricing source: ✓ = web-verified, API = AWS Pricing API, LLM = LiteLLM community, Est = unknown" style={{ opacity: 0.6 }}>{
                            displayActive.metrics.pricingSource === "verified" ? "✅ Verified" :
                            displayActive.metrics.pricingSource === "aws_api" ? "📡 API" :
                            displayActive.metrics.pricingSource === "litellm" ? "📊 LiteLLM" : "⚠️ Est"
                          }</span>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "14px", marginTop: "6px", color: dark ? "#c4c4c4" : "#4b5563", opacity: (displayActive.metrics.cacheReadTokens > 0 || displayActive.metrics.cacheWriteTokens > 0) ? 1 : 0.5 }}>
                          <span title="Cache Read: Tokens served from Bedrock's prompt cache. Charged at ~90% discount vs standard input rate." style={{ fontWeight: 600 }}>📖 Cache Read <span style={{ color: (displayActive.metrics.cacheReadTokens || 0) > 0 ? "#22c55e" : "inherit" }}>{displayActive.metrics.cacheReadTokens || 0}</span></span>
                          <span style={{ opacity: 0.3 }}>|</span>
                          <span title="Cache Write: Tokens written to cache for future reuse. May cost more than standard input." style={{ fontWeight: 600 }}>✏️ Cache Write <span>{displayActive.metrics.cacheWriteTokens || 0}</span></span>
                          <span style={{ opacity: 0.3 }}>|</span>
                          <span title="Cache Hit %: cacheReadTokens ÷ (inputTokens + cacheRead + cacheWrite) × 100. Higher = more reuse, lower cost." style={{ fontWeight: 700 }}>🎯 Cache Hit <span style={{ color: (displayActive.metrics.cacheHitRate || 0) > 50 ? "#22c55e" : "inherit" }}>{displayActive.metrics.cacheHitRate || 0}%</span></span>
                          <span style={{ opacity: 0.3 }}>|</span>
                          <span title="Saved: Net savings from caching = read savings − write overhead." style={{ fontWeight: 700 }}>💚 Saved <span style={{ color: (displayActive.metrics.cacheSavingsUsd || 0) > 0 ? "#22c55e" : "inherit" }}>${(displayActive.metrics.cacheSavingsUsd || 0) < 0.001 ? (displayActive.metrics.cacheSavingsUsd || 0).toFixed(6) : (displayActive.metrics.cacheSavingsUsd || 0).toFixed(4)}</span></span>
                        </div>
                      </div>
                    )}
                    {/* Copy button */}
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
                      <CopyBtn text={displayActive.response || ""} copyKey={`resp-${i}-${activeModelIdx}`} />
                    </div>
                    {/* Bottom toggle — original / optimized */}
                    {hasOriginal && activeModelIdx < uniqueModels.length && (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px", padding: "6px 10px", borderRadius: "8px", backgroundColor: dark ? "rgba(34,197,94,0.06)" : "rgba(34,197,94,0.04)", border: `1px solid ${dark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.1)"}` }}>
                        <span style={{ fontSize: "10px", fontWeight: 600, color: isShowingOriginal ? t.muted : "#22c55e", letterSpacing: "0.02em" }}>
                          {isShowingOriginal ? "📄 Response based on Original Prompt" : "✨ Response based on Optimized Prompt"}
                        </span>
                        <div
                          onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? { ...msg, [showOriginalKey]: !isShowingOriginal } : msg))}
                          style={{ width: "32px", height: "16px", borderRadius: "8px", position: "relative", backgroundColor: isShowingOriginal ? (dark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)") : "#22c55e", cursor: "pointer", transition: "background-color 0.2s", flexShrink: 0 }}>
                          <div style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: "#fff", position: "absolute", top: "2px", left: isShowingOriginal ? "2px" : "18px", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                        </div>
                        <span style={{ fontSize: "9px", color: t.muted, fontStyle: "italic" }}>
                          {isShowingOriginal ? "Switch to optimized →" : "← Switch to original"}
                        </span>
                        <button onClick={() => {
                          const targetMid = active.model || activeModelId;
                          setMessages(prev => prev.map((msg, mi) => {
                            if (mi !== i) return msg;
                            const updates = { ...msg, activeTab: optTabIdx };
                            for (const uid of uniqueModels) {
                              updates[`opt_${i}_${uid}`] = uid === targetMid;
                            }
                            return updates;
                          }));
                        }}
                          style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: "4px", padding: "3px 8px", borderRadius: "6px", border: "1px solid rgba(34,197,94,0.3)", backgroundColor: "transparent", color: "#22c55e", cursor: "pointer", fontSize: "9px", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>
                          ✨ View optimized system prompt →
                        </button>
                        {isRag && active.kbKey && showChunksTab && (
                        <button onClick={() => {
                          const chunksTabIdx = uniqueModels.length + (showCompare ? 1 : 0);
                          setMessages(prev => prev.map((msg, mi) => mi === i ? {
                            ...msg, activeTab: chunksTabIdx, [`chunks_${active.kbKey}`]: true,
                          } : msg));
                        }}
                          style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "3px 8px", borderRadius: "6px", border: `1px solid ${t.accent}33`, backgroundColor: "transparent", color: t.accent, cursor: "pointer", fontSize: "9px", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>
                          📄 View retrieved chunks →
                        </button>
                        )}
                      </div>
                    )}
                  </div>
                  ) : activeModelIdx === uniqueModels.length && showCompare ? (
                  /* Compare tab content */
                  (() => {
                    const modelAgg = {};
                    const originalModelAgg = {};
                    const hasOrig = m.originalResults && m.originalResults.length > 0 && m.perModelPrompts;
                    for (const mid of uniqueModels) {
                      const mrs = m.results.filter(r => r.model === mid && r.metrics?.latencyMs);
                      const n = mrs.length || 1;
                      modelAgg[mid] = {
                        avgLatency: Math.round(mrs.reduce((s, r) => s + (r.metrics?.latencyMs || 0), 0) / n),
                        avgTtft: Math.round(mrs.reduce((s, r) => s + (r.metrics?.ttftMs || 0), 0) / n),
                        sumInput: mrs.reduce((s, r) => s + (r.metrics?.inputTokens || 0), 0),
                        sumOutput: mrs.reduce((s, r) => s + (r.metrics?.outputTokens || 0), 0),
                        sumTotal: mrs.reduce((s, r) => s + (r.metrics?.totalTokens || 0), 0),
                        sumCacheRead: mrs.reduce((s, r) => s + (r.metrics?.cacheReadTokens || 0), 0),
                        sumCacheWrite: mrs.reduce((s, r) => s + (r.metrics?.cacheWriteTokens || 0), 0),
                        avgCacheHit: Math.round(mrs.reduce((s, r) => s + (r.metrics?.cacheHitRate || 0), 0) / n * 10) / 10,
                        sumCost: mrs.reduce((s, r) => s + (r.metrics?.costUsd || 0), 0),
                        sumSaved: mrs.reduce((s, r) => s + (r.metrics?.cacheSavingsUsd || 0), 0),
                      };
                      if (hasOrig) {
                        const omrs = m.originalResults.filter(r => r.model === mid && r.metrics?.latencyMs);
                        const on = omrs.length || 1;
                        originalModelAgg[mid] = {
                          avgLatency: Math.round(omrs.reduce((s, r) => s + (r.metrics?.latencyMs || 0), 0) / on),
                          avgTtft: Math.round(omrs.reduce((s, r) => s + (r.metrics?.ttftMs || 0), 0) / on),
                          sumInput: omrs.reduce((s, r) => s + (r.metrics?.inputTokens || 0), 0),
                          sumOutput: omrs.reduce((s, r) => s + (r.metrics?.outputTokens || 0), 0),
                          sumTotal: omrs.reduce((s, r) => s + (r.metrics?.totalTokens || 0), 0),
                          sumCacheRead: omrs.reduce((s, r) => s + (r.metrics?.cacheReadTokens || 0), 0),
                          sumCacheWrite: omrs.reduce((s, r) => s + (r.metrics?.cacheWriteTokens || 0), 0),
                          avgCacheHit: Math.round(omrs.reduce((s, r) => s + (r.metrics?.cacheHitRate || 0), 0) / on * 10) / 10,
                          sumCost: omrs.reduce((s, r) => s + (r.metrics?.costUsd || 0), 0),
                          sumSaved: omrs.reduce((s, r) => s + (r.metrics?.cacheSavingsUsd || 0), 0),
                        };
                      }
                    }
                    const deltaFmt = (opt, orig, lower) => {
                      const d = opt - orig;
                      if (Math.abs(d) < 0.000001) return { text: "—", color: t.muted };
                      const better = lower ? d < 0 : d > 0;
                      let text;
                      if (Math.abs(d) < 0.001) text = (d > 0 ? "+" : "") + d.toFixed(6);
                      else if (Math.abs(d) < 1) text = (d > 0 ? "+" : "") + d.toFixed(4);
                      else text = (d > 0 ? "+" : "") + Math.round(d);
                      return { text, color: better ? "#22c55e" : "#ef4444" };
                    };
                    return (
                  <div style={{ padding: "16px", fontSize: "13px", color: t.text }}>
                    <h3 style={{ margin: "0 0 12px", fontSize: "15px", color: t.accent }}>📊 Model Comparison</h3>

                    {/* Optimization summary banner */}
                    {hasOrig && (() => {
                      const avgLatOpt = uniqueModels.reduce((s, mid) => s + modelAgg[mid].avgLatency, 0) / uniqueModels.length;
                      const avgLatOrig = uniqueModels.reduce((s, mid) => s + (originalModelAgg[mid]?.avgLatency || 0), 0) / uniqueModels.length;
                      const avgTtftOpt = uniqueModels.reduce((s, mid) => s + modelAgg[mid].avgTtft, 0) / uniqueModels.length;
                      const avgTtftOrig = uniqueModels.reduce((s, mid) => s + (originalModelAgg[mid]?.avgTtft || 0), 0) / uniqueModels.length;
                      const totalCostOpt = uniqueModels.reduce((s, mid) => s + modelAgg[mid].sumCost, 0);
                      const totalCostOrig = uniqueModels.reduce((s, mid) => s + (originalModelAgg[mid]?.sumCost || 0), 0);
                      const pctChange = (opt, orig) => {
                        if (!orig || orig === 0) return null;
                        return Math.round(((opt - orig) / orig) * 100);
                      };
                      const latPct = pctChange(avgLatOpt, avgLatOrig);
                      const ttftPct = pctChange(avgTtftOpt, avgTtftOrig);
                      const costPct = pctChange(totalCostOpt, totalCostOrig);
                      const chip = (label, pct, lower) => {
                        if (pct === null) return null;
                        const better = lower ? pct < 0 : pct > 0;
                        const color = better ? "#22c55e" : pct === 0 ? t.muted : "#ef4444";
                        const arrow = pct < 0 ? "↓" : pct > 0 ? "↑" : "";
                        return (
                          <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "8px 18px", borderRadius: "8px", backgroundColor: better ? "rgba(34,197,94,0.1)" : pct === 0 ? "transparent" : "rgba(239,68,68,0.1)", border: `1px solid ${better ? "rgba(34,197,94,0.2)" : pct === 0 ? t.border : "rgba(239,68,68,0.2)"}`, flex: "1 1 0" }}>
                            <span style={{ fontSize: "12px", color: t.muted, fontWeight: 600 }}>{label}</span>
                            <span style={{ fontSize: "14px", fontWeight: 700, color }}>{pct > 0 ? "+" : ""}{pct}% {arrow}</span>
                          </span>
                        );
                      };
                      return (
                        <div style={{ marginBottom: "14px", padding: "10px 14px", borderRadius: "10px", background: dark ? "linear-gradient(135deg, rgba(34,197,94,0.06), rgba(34,197,94,0.02))" : "linear-gradient(135deg, rgba(34,197,94,0.04), rgba(34,197,94,0.01))", border: `1px solid ${dark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.1)"}` }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                            <span style={{ fontSize: "12px" }}>✨</span>
                            <span style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e" }}>Auto-Optimize Impact</span>
                            <span style={{ fontSize: "9px", color: t.muted, fontStyle: "italic", marginLeft: "4px" }}>Optimized vs Original (avg across models)</span>
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                            {chip("Latency", latPct, true)}
                            {chip("TTFT", ttftPct, true)}
                            {chip("Cost", costPct, true)}
                          </div>
                        </div>
                      );
                    })()}


                    {/* Latency & TTFT chart — grouped bars when optimized */}
                    <div style={{ marginBottom: "12px", border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`, borderRadius: "8px", overflow: "hidden" }}>
                      <button onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? {...msg, showLatency: !(msg.showLatency ?? true)} : msg))}
                        style={{ width: "100%", padding: "8px 12px", display: "flex", alignItems: "center", gap: "8px", border: "none", backgroundColor: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", cursor: "pointer", color: t.text }}>
                        <span style={{ fontSize: "10px", transition: "transform 0.2s", transform: (m.showLatency ?? true) ? "rotate(90deg)" : "rotate(0)" }}>▶</span>
                        <span style={{ fontSize: "13px", fontWeight: 700, color: t.accent }}>⏱ Avg Latency & TTFT (ms)</span>
                        {isRag && <span style={{ fontSize: "10px", color: "#00d4ff", fontWeight: 500, marginLeft: "8px" }}>Perf data shown only for the LLM call and doesn't include the RAG pipeline</span>}
                        <span style={{ marginLeft: "auto", fontSize: "10px", padding: "2px 8px", borderRadius: "10px", backgroundColor: dark ? "rgba(232,101,26,0.12)" : "rgba(9,114,211,0.08)", color: t.accent, fontWeight: 600 }}>Lower is better</span>
                      </button>
                      {(m.showLatency ?? true) && (<div style={{ padding: "8px 12px 10px" }}>
                        {(() => {
                          const allLats = uniqueModels.flatMap(mid => [modelAgg[mid].avgLatency, ...(hasOrig ? [originalModelAgg[mid]?.avgLatency || 0] : [])]);
                          const maxLat = Math.max(...allLats);
                          return uniqueModels.map((mid, ri) => (
                          <div key={ri} style={{ marginBottom: hasOrig ? "8px" : "4px" }}>
                            {/* Optimized bar */}
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: hasOrig ? "2px" : "0" }}>
                              <span style={{ width: "100px", fontSize: "11px", textAlign: "right", flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{getModelName(mid)}{hasOrig ? " ✨" : ""}</span>
                              <div style={{ flex: 1, height: "18px", backgroundColor: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)", borderRadius: "4px", overflow: "hidden", position: "relative" }}>
                                <AnimatedBar pct={maxLat > 0 ? (modelAgg[mid].avgLatency / maxLat * 100) : 0} color={t.accent} style={{ height: "100%", borderRadius: "4px", opacity: 0.6 }} />
                                <AnimatedBar pct={maxLat > 0 ? (modelAgg[mid].avgTtft / maxLat * 100) : 0} color="#22c55e" style={{ position: "absolute", top: 0, left: 0, height: "100%", borderRadius: "4px" }} />
                              </div>
                              <span style={{ fontSize: "10px", fontWeight: 600, width: "90px", color: t.muted, textAlign: "right" }}>{modelAgg[mid].avgLatency} / {modelAgg[mid].avgTtft}ms</span>
                            </div>
                            {/* Original bar (if available) */}
                            {hasOrig && originalModelAgg[mid] && (
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{ width: "100px", fontSize: "10px", textAlign: "right", flexShrink: 0, color: t.muted, fontStyle: "italic" }}>📄 Original</span>
                                <div style={{ flex: 1, height: "14px", backgroundColor: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", borderRadius: "4px", overflow: "hidden", position: "relative" }}>
                                  <AnimatedBar pct={maxLat > 0 ? (originalModelAgg[mid].avgLatency / maxLat * 100) : 0} color={t.accent} style={{ height: "100%", borderRadius: "4px", opacity: 0.3 }} />
                                  <AnimatedBar pct={maxLat > 0 ? (originalModelAgg[mid].avgTtft / maxLat * 100) : 0} color="#22c55e" style={{ position: "absolute", top: 0, left: 0, height: "100%", borderRadius: "4px", opacity: 0.4 }} />
                                </div>
                                <span style={{ fontSize: "10px", fontWeight: 600, width: "90px", color: t.muted, textAlign: "right", opacity: 0.7 }}>{originalModelAgg[mid].avgLatency} / {originalModelAgg[mid].avgTtft}ms</span>
                              </div>
                            )}
                          </div>
                        )); })()}
                        <div style={{ display: "flex", gap: "12px", marginTop: "4px", fontSize: "10px", color: t.muted }}>
                          <span>🟧 Latency</span><span>🟩 TTFT</span>
                          {hasOrig && <span style={{ marginLeft: "8px", fontStyle: "italic" }}>✨ = Optimized, 📄 = Original</span>}
                        </div>
                      </div>)}
                    </div>

                    {/* Cost chart — grouped bars when optimized */}
                    <div style={{ marginBottom: "12px", border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`, borderRadius: "8px", overflow: "hidden" }}>
                      <button onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? {...msg, showCost: !(msg.showCost ?? true)} : msg))}
                        style={{ width: "100%", padding: "8px 12px", display: "flex", alignItems: "center", gap: "8px", border: "none", backgroundColor: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", cursor: "pointer", color: t.text }}>
                        <span style={{ fontSize: "10px", transition: "transform 0.2s", transform: (m.showCost ?? true) ? "rotate(90deg)" : "rotate(0)" }}>▶</span>
                        <span style={{ fontSize: "13px", fontWeight: 700, color: t.accent }}>💰 Total Cost (USD)</span>
                        {isRag && <span style={{ fontSize: "10px", color: "#00d4ff", fontWeight: 500, marginLeft: "8px" }}>Cost shown only for the LLM call and doesn't include the RAG pipeline cost</span>}
                        <span style={{ marginLeft: "auto", fontSize: "10px", padding: "2px 8px", borderRadius: "10px", backgroundColor: dark ? "rgba(232,101,26,0.12)" : "rgba(9,114,211,0.08)", color: t.accent, fontWeight: 600 }}>Lower is better</span>
                      </button>
                      {(m.showCost ?? true) && (<div style={{ padding: "8px 12px 10px" }}>
                        {(() => {
                          const allCosts = uniqueModels.flatMap(mid => [modelAgg[mid].sumCost, ...(hasOrig ? [originalModelAgg[mid]?.sumCost || 0] : [])]);
                          const maxCost = Math.max(...allCosts);
                          return uniqueModels.map((mid, ri) => (
                          <div key={ri} style={{ marginBottom: hasOrig ? "8px" : "4px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: hasOrig ? "2px" : "0" }}>
                              <span style={{ width: "100px", fontSize: "11px", textAlign: "right", flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{getModelName(mid)}{hasOrig ? " ✨" : ""}</span>
                              <div style={{ flex: 1, height: "18px", backgroundColor: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)", borderRadius: "4px", overflow: "hidden" }}>
                                <AnimatedBar pct={maxCost > 0 ? (modelAgg[mid].sumCost / maxCost * 100) : 0} color={dark ? "#ffe156" : "#d97706"} style={{ height: "100%", borderRadius: "4px" }} />
                              </div>
                              <span style={{ fontSize: "11px", fontWeight: 600, width: "70px", color: dark ? "#ffe156" : "#d97706" }}>${modelAgg[mid].sumCost.toFixed(4)}</span>
                            </div>
                            {hasOrig && originalModelAgg[mid] && (
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{ width: "100px", fontSize: "10px", textAlign: "right", flexShrink: 0, color: t.muted, fontStyle: "italic" }}>📄 Original</span>
                                <div style={{ flex: 1, height: "14px", backgroundColor: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", borderRadius: "4px", overflow: "hidden" }}>
                                  <AnimatedBar pct={maxCost > 0 ? (originalModelAgg[mid].sumCost / maxCost * 100) : 0} color={dark ? "#ffe156" : "#d97706"} style={{ height: "100%", borderRadius: "4px", opacity: 0.35 }} />
                                </div>
                                <span style={{ fontSize: "11px", fontWeight: 600, width: "70px", color: dark ? "#ffe156" : "#d97706", opacity: 0.6 }}>${originalModelAgg[mid].sumCost.toFixed(4)}</span>
                              </div>
                            )}
                          </div>
                        )); })()}
                      </div>)}
                    </div>

                    {/* Comparison table - with Original + Delta columns when optimized */}
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11.5px", fontFamily: "'SF Mono', monospace" }}>
                        <thead>
                          <tr style={{ borderBottom: `2px solid ${t.border}` }}>
                            <th style={{ padding: "6px 8px", textAlign: "left", color: t.muted, fontWeight: 700 }}>Metric</th>
                            {uniqueModels.flatMap((mid, ri) => {
                              const cols = [
                                <th key={`opt-${ri}`} style={{ padding: "6px 8px", textAlign: "right", color: hasOrig ? "#22c55e" : t.accent, fontWeight: 700, fontSize: "10px", cursor: isRag ? "pointer" : "default" }}
                                  onClick={() => isRag && setMessages(prev => prev.map((msg, mi) => mi === i ? {...msg, [`expandModel_${mid}`]: !msg[`expandModel_${mid}`]} : msg))}>
                                  {isRag && <span style={{ fontSize: "9px", marginRight: "4px" }}>{m[`expandModel_${mid}`] ? "▼" : "▶"}</span>}
                                  {getModelName(mid)}{hasOrig ? " ✨" : ""}
                                </th>
                              ];
                              if (hasOrig) {
                                cols.push(
                                  <th key={`orig-${ri}`} style={{ padding: "6px 8px", textAlign: "right", color: t.muted, fontWeight: 600, fontSize: "10px", fontStyle: "italic" }}>📄 Orig</th>,
                                  <th key={`delta-${ri}`} style={{ padding: "6px 8px", textAlign: "right", color: t.muted, fontWeight: 600, fontSize: "10px" }}>Δ</th>
                                );
                              }
                              return cols;
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { label: "⚡ Avg TTFT", key: "ttftMs", fmt: v => `${v}ms`, aggKey: "avgTtft", lower: true },
                            { label: "⏱ Avg Latency", key: "latencyMs", fmt: v => `${v}ms`, aggKey: "avgLatency", lower: true },
                            { label: "🔼 Input Tokens", key: "inputTokens", fmt: v => v, aggKey: "sumInput", lower: true },
                            { label: "🔽 Output Tokens", key: "outputTokens", fmt: v => v, aggKey: "sumOutput", lower: true },
                            { label: "🔢 Total Tokens", key: "totalTokens", fmt: v => v, aggKey: "sumTotal", lower: true },
                            { label: "📖 Cache Read", key: "cacheReadTokens", fmt: v => v, aggKey: "sumCacheRead" },
                            { label: "✏️ Cache Write", key: "cacheWriteTokens", fmt: v => v, aggKey: "sumCacheWrite" },
                            { label: "🎯 Cache Hit", key: "cacheHitRate", fmt: v => `${v}%`, aggKey: "avgCacheHit" },
                            { label: "💰 Cost", key: "costUsd", fmt: v => `${v < 0.001 ? v.toFixed(6) : v.toFixed(4)}`, aggKey: "sumCost", lower: true },
                            { label: "💚 Saved", key: "cacheSavingsUsd", fmt: v => `${v < 0.001 ? v.toFixed(6) : v.toFixed(4)}`, aggKey: "sumSaved" },
                          ].flatMap((row, ri) => {
                            const rows = [
                              <tr key={`agg-${ri}`} style={{ borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}` }}>
                                <td style={{ padding: "5px 8px", color: t.muted, fontWeight: 600 }}>{row.label}</td>
                                {uniqueModels.flatMap((mid, ci) => {
                                  const cells = [
                                    <td key={`opt-${ci}`} style={{ padding: "5px 8px", textAlign: "right", color: t.text }}>{row.fmt(modelAgg[mid][row.aggKey])}</td>
                                  ];
                                  if (hasOrig && originalModelAgg[mid]) {
                                    const optVal = modelAgg[mid][row.aggKey];
                                    const origVal = originalModelAgg[mid][row.aggKey];
                                    const d = deltaFmt(optVal, origVal, row.lower);
                                    cells.push(
                                      <td key={`orig-${ci}`} style={{ padding: "5px 8px", textAlign: "right", color: t.muted, opacity: 0.7 }}>{row.fmt(origVal)}</td>,
                                      <td key={`delta-${ci}`} style={{ padding: "5px 8px", textAlign: "right", color: d.color, fontWeight: 600, fontSize: "10px" }}>{d.text}</td>
                                    );
                                  } else if (hasOrig) {
                                    cells.push(<td key={`orig-${ci}`}></td>, <td key={`delta-${ci}`}></td>);
                                  }
                                  return cells;
                                })}
                              </tr>
                            ];
                            if (isRag && uniqueModels.some(mid => m[`expandModel_${mid}`])) {
                              rows.push(
                                <tr key={`kb-${ri}`} style={{ backgroundColor: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)" }}>
                                  <td style={{ padding: "2px 8px" }}></td>
                                  {uniqueModels.flatMap((mid, ci) => {
                                    const expandCells = [];
                                    if (!m[`expandModel_${mid}`]) {
                                      expandCells.push(<td key={`opt-${ci}`}></td>);
                                      if (hasOrig) expandCells.push(<td key={`orig-${ci}`}></td>, <td key={`delta-${ci}`}></td>);
                                      return expandCells;
                                    }
                                    const mrs = m.results.filter(r => r.model === mid);
                                    expandCells.push(<td key={`opt-${ci}`} style={{ padding: "2px 4px", textAlign: "right", fontSize: "9px" }}>
                                      {mrs.map((r, ki) => {
                                        const kb = (r.kbKey || "").replace("default-","D·").replace("semantic-","S·").replace("titan","Ti").replace("cohere","Co");
                                        return <div key={ki} style={{ color: t.muted }}><span style={{ opacity: 0.6 }}>{kb}:</span> {row.fmt(r.metrics?.[row.key] ?? 0)}</div>;
                                      })}
                                    </td>);
                                    if (hasOrig) {
                                      expandCells.push(<td key={`orig-${ci}`} colSpan={2} style={{ padding: "2px 4px", textAlign: "right", fontSize: "9px" }}>
                                        {(m.originalResults || []).filter(r => r.model === mid).map((r, ki) => {
                                          const kb = (r.kbKey || "").replace("default-","D·").replace("semantic-","S·").replace("titan","Ti").replace("cohere","Co");
                                          return <div key={ki} style={{ color: t.muted, fontStyle: "italic" }}><span style={{ opacity: 0.6 }}>📄 {kb}:</span> {row.fmt(r.metrics?.[row.key] ?? 0)}</div>;
                                        })}
                                      </td>);
                                    }
                                    return expandCells;
                                  })}
                                </tr>
                              );
                            }
                            return rows;
                          })}
                        </tbody>
                      </table>
                    </div>
                    {/* Copy + Export buttons */}
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px", marginTop: "8px" }}>
                      <CopyBtn text={(() => {
                        let txt = "Model Comparison\n\n";
                        const hdr = ["Metric"];
                        uniqueModels.forEach(mid => { hdr.push(getModelName(mid) + (hasOrig ? " ✨" : "")); if (hasOrig) { hdr.push("Original"); hdr.push("Δ"); } });
                        txt += hdr.join("\t") + "\n";
                        const fmtDelta = (o, r, lower) => { const d = o - r; if (Math.abs(d) < 0.000001) return "—"; const s = (d > 0 ? "+" : "") + (Math.abs(d) < 1 ? d.toFixed(4) : Math.round(d)); return s; };
                        [{ l: "Avg TTFT", k: "avgTtft", f: v => v + "ms", lower: true }, { l: "Avg Latency", k: "avgLatency", f: v => v + "ms", lower: true },
                         { l: "Input Tokens", k: "sumInput", f: v => v, lower: true }, { l: "Output Tokens", k: "sumOutput", f: v => v, lower: true },
                         { l: "Total Tokens", k: "sumTotal", f: v => v, lower: true }, { l: "Cache Read", k: "sumCacheRead", f: v => v },
                         { l: "Cache Write", k: "sumCacheWrite", f: v => v }, { l: "Cache Hit", k: "avgCacheHit", f: v => v + "%" },
                         { l: "Cost", k: "sumCost", f: v => "$" + (v < 0.001 ? v.toFixed(6) : v.toFixed(4)), lower: true },
                         { l: "Saved", k: "sumSaved", f: v => "$" + (v < 0.001 ? v.toFixed(6) : v.toFixed(4)) },
                        ].forEach(r => { const row = [r.l]; uniqueModels.forEach(mid => { row.push(r.f(modelAgg[mid][r.k])); if (hasOrig && originalModelAgg[mid]) { row.push(r.f(originalModelAgg[mid][r.k])); row.push(fmtDelta(modelAgg[mid][r.k], originalModelAgg[mid][r.k], r.lower)); } else if (hasOrig) { row.push(""); row.push(""); } }); txt += row.join("\t") + "\n"; });
                        return txt;
                      })()} copyKey={`compare-${i}`} />
                      <button onClick={(e) => {
                        e.stopPropagation();
                        const ac = "#e8651a";
                        const allLats = uniqueModels.flatMap(mid => [modelAgg[mid].avgLatency, ...(hasOrig && originalModelAgg[mid] ? [originalModelAgg[mid].avgLatency] : [])]);
                        const maxLat = Math.max(...allLats);
                        const allCosts = uniqueModels.flatMap(mid => [modelAgg[mid].sumCost, ...(hasOrig && originalModelAgg[mid] ? [originalModelAgg[mid].sumCost] : [])]);
                        const maxCost = Math.max(...allCosts);
                        const barRow = (label, val, max, color, suffix) => {
                          const pct = max > 0 ? (val / max * 100) : 0;
                          return `<div style="display:flex;align-items:center;gap:8px;margin:4px 0"><span style="width:110px;font-size:12px;text-align:right;flex-shrink:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${label}</span><div style="flex:1;height:18px;background:rgba(255,255,255,0.05);border-radius:4px;overflow:hidden"><div style="width:${pct}%;height:100%;background:${color};border-radius:4px;opacity:0.8"></div></div><span style="font-size:11px;font-weight:600;width:80px;text-align:right;color:${color}">${suffix}</span></div>`;
                        };
                        let html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Compare Stats</title>
<style>body{font-family:-apple-system,sans-serif;background:#0d0a1a;color:#e0e0e0;padding:32px;max-width:900px;margin:0 auto}
h2,h3{color:${ac}}table{border-collapse:collapse;width:100%;margin:12px 0}th,td{border:1px solid rgba(255,255,255,0.12);padding:8px 12px;text-align:left;font-size:13px}
th{background:rgba(232,101,26,0.12);color:${ac};font-weight:700;font-size:11px}tr:hover{background:rgba(232,101,26,0.06)}
.section{margin:20px 0;padding:16px;border:1px solid rgba(255,255,255,0.08);border-radius:8px;background:rgba(255,255,255,0.02)}
.badge{font-size:10px;padding:2px 8px;border-radius:10px;background:rgba(232,101,26,0.12);color:${ac};font-weight:600;margin-left:8px}
.green{color:#22c55e}.red{color:#ef4444}.muted{color:#888;font-style:italic}</style></head><body>
<h2>\ud83d\udcca Model Comparison</h2>\n`;
                        // Latency chart
                        html += `<div class="section"><h3>\u23f1 Avg Latency & TTFT (ms) <span class="badge">Lower is better</span></h3>\n`;
                        uniqueModels.forEach(mid => {
                          html += barRow(getModelName(mid) + (hasOrig ? " \u2728" : ""), modelAgg[mid].avgLatency, maxLat, ac, modelAgg[mid].avgLatency + " / " + modelAgg[mid].avgTtft + "ms");
                          if (hasOrig && originalModelAgg[mid]) {
                            html += barRow("\ud83d\udcc4 Original", originalModelAgg[mid].avgLatency, maxLat, "#888", originalModelAgg[mid].avgLatency + " / " + originalModelAgg[mid].avgTtft + "ms");
                          }
                        });
                        html += `<div style="margin-top:6px;font-size:10px;color:#888">\ud83d\udfe7 Latency &nbsp; \ud83d\udfe9 TTFT${hasOrig ? " &nbsp; \u2728 = Optimized, \ud83d\udcc4 = Original" : ""}</div></div>\n`;
                        // Cost chart
                        html += `<div class="section"><h3>\ud83d\udcb0 Total Cost (USD) <span class="badge">Lower is better</span></h3>\n`;
                        uniqueModels.forEach(mid => {
                          html += barRow(getModelName(mid) + (hasOrig ? " \u2728" : ""), modelAgg[mid].sumCost, maxCost, "#ffe156", "$" + (modelAgg[mid].sumCost < 0.001 ? modelAgg[mid].sumCost.toFixed(6) : modelAgg[mid].sumCost.toFixed(4)));
                          if (hasOrig && originalModelAgg[mid]) {
                            html += barRow("\ud83d\udcc4 Original", originalModelAgg[mid].sumCost, maxCost, "#888", "$" + (originalModelAgg[mid].sumCost < 0.001 ? originalModelAgg[mid].sumCost.toFixed(6) : originalModelAgg[mid].sumCost.toFixed(4)));
                          }
                        });
                        html += `</div>\n`;
                        // Table with Original + Delta columns
                        const thModels = uniqueModels.map(mid => {
                          let cols = `<th>${getModelName(mid)}${hasOrig ? " \u2728" : ""}</th>`;
                          if (hasOrig) cols += `<th class="muted">\ud83d\udcc4 Orig</th><th>\u0394</th>`;
                          return cols;
                        }).join("");
                        html += `<h3>\ud83d\udccb Detailed Metrics</h3><table><thead><tr><th>Metric</th>${thModels}</tr></thead><tbody>`;
                        const fmtD = (o, r, lower) => { const d = o - r; if (Math.abs(d) < 0.000001) return `<span class="muted">\u2014</span>`; const better = lower ? d < 0 : d > 0; const cls = better ? "green" : "red"; const s = (d > 0 ? "+" : "") + (Math.abs(d) < 1 ? d.toFixed(4) : Math.round(d)); return `<span class="${cls}">${s}</span>`; };
                        [{ l: "\u26a1 Avg TTFT", k: "avgTtft", f: v => v + "ms", lower: true }, { l: "\u23f1 Avg Latency", k: "avgLatency", f: v => v + "ms", lower: true },
                         { l: "\ud83d\udd3c Input Tokens", k: "sumInput", f: v => v, lower: true }, { l: "\ud83d\udd3d Output Tokens", k: "sumOutput", f: v => v, lower: true },
                         { l: "\ud83d\udd22 Total Tokens", k: "sumTotal", f: v => v, lower: true }, { l: "\ud83d\udcd6 Cache Read", k: "sumCacheRead", f: v => v },
                         { l: "\u270f\ufe0f Cache Write", k: "sumCacheWrite", f: v => v }, { l: "\ud83c\udfaf Cache Hit", k: "avgCacheHit", f: v => v + "%" },
                         { l: "\ud83d\udcb0 Cost", k: "sumCost", f: v => "$" + (v < 0.001 ? v.toFixed(6) : v.toFixed(4)), lower: true },
                         { l: "\ud83d\udc9a Saved", k: "sumSaved", f: v => "$" + (v < 0.001 ? v.toFixed(6) : v.toFixed(4)) },
                        ].forEach(r => {
                          let row = `<tr><td>${r.l}</td>`;
                          uniqueModels.forEach(mid => {
                            row += `<td style="text-align:right">${r.f(modelAgg[mid][r.k])}</td>`;
                            if (hasOrig && originalModelAgg[mid]) {
                              row += `<td style="text-align:right" class="muted">${r.f(originalModelAgg[mid][r.k])}</td>`;
                              row += `<td style="text-align:right;font-weight:600">${fmtD(modelAgg[mid][r.k], originalModelAgg[mid][r.k], r.lower)}</td>`;
                            } else if (hasOrig) {
                              row += `<td></td><td></td>`;
                            }
                          });
                          html += row + "</tr>";
                        });
                        html += "</tbody></table></body></html>";
                        const blob = new Blob([html], { type: "text/html" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a"); a.href = url; a.download = `compare-stats-${new Date().toISOString().slice(0, 10)}.html`; a.click(); URL.revokeObjectURL(url);
                      }}
                        style={{ display: "inline-flex", alignItems: "center", gap: "3px", padding: "3px 8px", borderRadius: "6px", border: `1px solid ${t.border}`, backgroundColor: "transparent", color: t.muted, cursor: "pointer", fontSize: "10px", fontWeight: 600 }}>
                        ⬇ HTML
                      </button>
                    </div>
                  </div>);})()
                  ) : showChunksTab && activeModelIdx === uniqueModels.length + (showCompare ? 1 : 0) ? (
                  /* Retrieved Chunks tab */
                  <div style={{ padding: "16px", fontSize: "13px", color: t.text }}>
                    <h3 style={{ margin: "0 0 12px", fontSize: "15px", color: t.accent }}>📄 Retrieved Chunks by KB Configuration</h3>
                    <p style={{ fontSize: "11px", color: t.muted, margin: "0 0 12px" }}>Text chunks retrieved from each Knowledge Base for this query</p>
                    {(m.kbKeys || []).map((kbKey, kbIdx) => {
                      const chunks = (m.ragChunks || {})[kbKey] || [];
                      const kbLabel = (kbKey || "").replace("default-", "Default · ").replace("semantic-", "Semantic · ").replace("titan", "Titan V2").replace("cohere", "Cohere ML");
                      const expandKey = `chunks_${kbKey}`;
                      const isExpanded = m[expandKey] !== undefined ? m[expandKey] : kbIdx === 0;
                      return (
                        <div key={kbKey} style={{ marginBottom: "8px", border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`, borderRadius: "8px", overflow: "hidden" }}>
                          <button onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? {...msg, [expandKey]: !isExpanded} : msg))}
                            style={{ width: "100%", padding: "8px 12px", display: "flex", alignItems: "center", gap: "8px", border: "none", backgroundColor: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", cursor: "pointer", color: t.text }}>
                            <span style={{ fontSize: "10px", transition: "transform 0.2s", transform: isExpanded ? "rotate(90deg)" : "rotate(0)" }}>▶</span>
                            <span style={{ fontSize: "12px", fontWeight: 700, color: t.accent }}>🔍 {kbLabel}</span>
                            <span style={{ marginLeft: "auto", fontSize: "10px", padding: "2px 8px", borderRadius: "10px", backgroundColor: dark ? "rgba(232,101,26,0.12)" : "rgba(9,114,211,0.08)", color: t.accent, fontWeight: 600 }}>{chunks.length} chunks</span>
                          </button>
                          {isExpanded && (<div style={{ padding: "8px 12px" }}>
                            {chunks.length === 0 ? (<div style={{ fontSize: "11px", color: t.muted, padding: "8px 0" }}>No chunks retrieved</div>
                            ) : chunks.map((chunk, ci) => {
                              const chunkExpandKey = `chunk_${kbKey}_${ci}`;
                              const chunkExpanded = m[chunkExpandKey] !== undefined ? m[chunkExpandKey] : ci === 0;
                              return (
                              <div key={ci} style={{ marginBottom: "6px", borderRadius: "6px", backgroundColor: dark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.02)", border: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"}`, overflow: "hidden" }}>
                                <button onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? {...msg, [chunkExpandKey]: !chunkExpanded} : msg))}
                                  style={{ width: "100%", padding: "6px 10px", display: "flex", alignItems: "center", gap: "6px", border: "none", backgroundColor: "transparent", cursor: "pointer", color: t.text }}>
                                  <span style={{ fontSize: "9px", transition: "transform 0.2s", transform: chunkExpanded ? "rotate(90deg)" : "rotate(0)" }}>▶</span>
                                  <span style={{ fontSize: "10px", color: t.muted }}>Chunk {ci + 1}{chunk.source ? ` · ${chunk.source}` : ""}</span>
                                  <span style={{ marginLeft: "auto", fontSize: "10px", fontWeight: 600, color: chunk.score >= 0.5 ? "#22c55e" : chunk.score >= 0.3 ? "#eab308" : t.muted }}>Score: {chunk.score}</span>
                                </button>
                                {chunkExpanded && (<div style={{ padding: "4px 10px 8px 24px", fontSize: "11px", color: t.text, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{chunk.text}</div>)}
                              </div>);
                            })}
                          </div>)}
                        </div>
                      );
                    })}
                    {/* Copy button for chunks */}
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
                      <CopyBtn text={(() => {
                        let txt = "Retrieved Chunks\n\n";
                        (m.kbKeys || []).forEach(kbKey => {
                          const chunks = (m.ragChunks || {})[kbKey] || [];
                          txt += `--- ${kbKey} (${chunks.length} chunks) ---\n`;
                          chunks.forEach((c, ci) => { txt += `[${ci + 1}] Score: ${c.score || "N/A"}\n${c.text}\n\n`; });
                        });
                        return txt;
                      })()} copyKey={`chunks-${i}`} />
                    </div>
                  </div>
                  ) : showOptTab && activeModelIdx === optTabIdx ? (
                  /* Optimized Prompts tab */
                  <div style={{ padding: "16px", fontSize: "13px", color: t.text }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
                      <div>
                        <h3 style={{ margin: "0 0 4px", fontSize: "15px", color: "#22c55e" }}>✨ Optimized System Prompts</h3>
                        <p style={{ fontSize: "11px", color: t.muted, margin: 0 }}>Auto-optimized system prompts tailored for each model family</p>
                      </div>
                      <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "10px", color: (m[`showDiff_${i}`] !== false) ? "#22c55e" : t.muted, fontWeight: 600 }}>
                        Show Diff
                        <div onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? {...msg, [`showDiff_${i}`]: msg[`showDiff_${i}`] === false ? true : false} : msg))} style={{
                          width: "30px", height: "16px", borderRadius: "8px", position: "relative",
                          backgroundColor: (m[`showDiff_${i}`] !== false) ? "#22c55e" : (dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"),
                          transition: "background-color 0.2s", cursor: "pointer",
                        }}>
                          <div style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: "#fff", position: "absolute", top: "2px", left: (m[`showDiff_${i}`] !== false) ? "16px" : "2px", transition: "left 0.2s", boxShadow: "0 1px 2px rgba(0,0,0,0.2)" }} />
                        </div>
                      </label>
                    </div>

                    {/* Original System Prompt */}
                    {m.originalPrompt && (
                      <div style={{ marginBottom: "10px", borderRadius: "8px", border: `1px solid ${t.border}`, overflow: "hidden" }}>
                        <button onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? {...msg, [`origExp_${i}`]: !msg[`origExp_${i}`]} : msg))}
                          style={{ width: "100%", padding: "8px 12px", display: "flex", alignItems: "center", gap: "8px", border: "none", backgroundColor: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", cursor: "pointer", color: t.text, textAlign: "left" }}>
                          <span style={{ fontSize: "10px", transform: m[`origExp_${i}`] ? "rotate(90deg)" : "rotate(0)", transition: "transform 0.2s" }}>▶</span>
                          <span style={{ fontSize: "12px", fontWeight: 600, color: t.accent }}>📄 Original System Prompt</span>
                          <span style={{ fontSize: "10px", color: t.muted, marginLeft: "auto" }}>{m.originalPrompt.length} chars</span>
                        </button>
                        {m[`origExp_${i}`] && (
                          <div style={{ padding: "10px 12px", borderTop: `1px solid ${t.border}`, fontSize: "11px", color: t.muted, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "200px", overflowY: "auto", lineHeight: 1.5, backgroundColor: dark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.02)" }}>
                            {m.originalPrompt}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Per-model optimized prompts */}
                    {uniqueModels.map((mid, midx) => {
                      const optPrompt = m.perModelPrompts[mid];
                      if (!optPrompt) return null;
                      const optKey = `opt_${i}_${mid}`;
                      const optExp = m[optKey] !== undefined ? m[optKey] : false;
                      const showDiff = (m[`showDiff_${i}`] !== false) && m.originalPrompt;
                      return (
                        <div key={mid} style={{ marginBottom: "8px", borderRadius: "8px", border: `1px solid ${dark ? "rgba(34,197,94,0.15)" : "rgba(22,163,74,0.12)"}`, overflow: "hidden" }}>
                          <button onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? {...msg, [optKey]: !optExp} : msg))}
                            style={{ width: "100%", padding: "8px 12px", display: "flex", alignItems: "center", gap: "8px", border: "none", backgroundColor: dark ? "rgba(34,197,94,0.05)" : "rgba(22,163,74,0.03)", cursor: "pointer", color: t.text, textAlign: "left" }}>
                            <span style={{ fontSize: "10px", transform: optExp ? "rotate(90deg)" : "rotate(0)", transition: "transform 0.2s" }}>▶</span>
                            <span style={{ fontSize: "12px", fontWeight: 600, color: "#22c55e" }}>✨ {getModelName(mid)}</span>
                            <span style={{ fontSize: "10px", color: t.muted, marginLeft: "auto" }}>{optPrompt.length} chars</span>
                          </button>
                          {optExp && (
                            <div style={{ padding: "10px 12px", borderTop: `1px solid ${dark ? "rgba(34,197,94,0.1)" : "rgba(22,163,74,0.08)"}`, fontSize: "11px", maxHeight: "300px", overflowY: "auto", lineHeight: 1.5, backgroundColor: dark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.02)" }}>
                              {showDiff ? (
                                <div>
                                  <div style={{ fontSize: "10px", color: "#22c55e", fontWeight: 600, marginBottom: "6px" }}>System Prompt — changes from baseline highlighted</div>
                                  <pre style={{ fontSize: "11px", whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0, fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", lineHeight: 1.6, padding: "10px", borderRadius: "8px", backgroundColor: dark ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.04)", border: `1px solid ${t.border}`, maxHeight: "280px", overflowY: "auto" }}>
                                    {wordDiff(m.originalPrompt, optPrompt).map((op, oi) => (
                                      op.type === "same" ? <span key={oi}>{op.text}</span> :
                                      op.type === "add" ? <span key={oi} style={{ backgroundColor: "rgba(34,197,94,0.2)", color: "#22c55e", borderRadius: "2px" }}>{op.text}</span> :
                                      <span key={oi} style={{ backgroundColor: "rgba(239,68,68,0.15)", color: "#ef4444", textDecoration: "line-through", borderRadius: "2px", opacity: 0.7 }}>{op.text}</span>
                                    ))}
                                  </pre>
                                </div>
                              ) : (
                                <div style={{ color: t.muted, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{optPrompt}</div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
                      <CopyBtn text={uniqueModels.map(mid => `=== ${getModelName(mid)} ===\n${m.perModelPrompts[mid] || "N/A"}`).join("\n\n")} copyKey={`opt-${i}`} />
                    </div>
                  </div>
                  ) : null}


                  {/* Bottom navigation */}
                  {uniqueModels.length > 1 && m.results.some(r => r.metrics?.latencyMs) && (
                    <div style={{ borderTop: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`, backgroundColor: dark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.02)" }}>
                      {/* Bottom KB pills first (RAG mode) */}
                      {isRag && activeModelIdx < uniqueModels.length && (() => {
                        const modelResults = m.results.filter(r => r.model === activeModelId);
                        return (
                          <div style={{ display: "flex", overflowX: "auto", gap: "6px", padding: "6px 12px" }}>
                            {modelResults.map((r, ki) => {
                              const kbLabel = (r.kbKey || "")
                                .replace("default-", "Default · ").replace("semantic-", "Semantic · ")
                                .replace("titan", "Titan").replace("cohere", "Cohere");
                              return (
                                <button key={ki}
                                  onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? {...msg, activeKbTab: {...(msg.activeKbTab || {}), [activeModelId]: ki}} : msg))}
                                  style={{
                                    padding: "4px 10px", borderRadius: "12px", border: `1px solid ${ki === activeKbIdx ? t.accent : t.border}`,
                                    backgroundColor: ki === activeKbIdx ? (dark ? "rgba(232,101,26,0.15)" : "rgba(9,114,211,0.08)") : "transparent",
                                    color: ki === activeKbIdx ? t.accent : t.muted, cursor: "pointer", fontSize: "10px", fontWeight: ki === activeKbIdx ? 700 : 400,
                                    whiteSpace: "nowrap", transition: "all 0.15s",
                                  }}>
                                  🔍 {kbLabel}
                                </button>
                              );
                            })}
                          </div>
                        );
                      })()}
                      {/* Bottom model tabs */}
                      <div style={{ display: "flex", overflowX: "auto", borderTop: isRag && activeModelIdx < uniqueModels.length ? `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"}` : "none" }}>
                        {uniqueModels.map((mid, ri) => (
                          <button key={ri} className={`tab-btn${ri === activeModelIdx ? " active" : ""}`} onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? {...msg, activeTab: ri} : msg))}
                            style={{ padding: "8px 14px", border: "none", borderTop: ri === activeModelIdx ? `2px solid ${t.accent}` : "2px solid transparent", backgroundColor: "transparent", color: ri === activeModelIdx ? t.accent : t.muted, cursor: "pointer", fontSize: "12px", fontWeight: ri === activeModelIdx ? 700 : 400, whiteSpace: "nowrap", transition: "all 0.15s" }}>
                            💬 {getModelName(mid)}
                          </button>
                        ))}
                        {showCompare && (
                          <button className={`tab-btn${activeModelIdx === uniqueModels.length ? " active" : ""}`} onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? {...msg, activeTab: uniqueModels.length} : msg))}
                            style={{ padding: "8px 14px", border: "none", borderTop: activeModelIdx === uniqueModels.length ? `2px solid ${dark ? "#00d4ff" : "#6366f1"}` : `1px solid ${dark ? "rgba(0,212,255,0.2)" : "rgba(99,102,241,0.2)"}`, backgroundColor: activeModelIdx === uniqueModels.length ? (dark ? "rgba(0,212,255,0.08)" : "rgba(99,102,241,0.06)") : "transparent", color: activeModelIdx === uniqueModels.length ? (dark ? "#00d4ff" : "#6366f1") : (dark ? "rgba(0,212,255,0.5)" : "rgba(99,102,241,0.5)"), cursor: "pointer", fontSize: "11px", fontWeight: activeModelIdx === uniqueModels.length ? 700 : 500, whiteSpace: "nowrap" }}>
                            📊 Compare Stats
                          </button>
                        )}
                        {showChunksTab && (
                          <button className={`tab-btn${activeModelIdx === uniqueModels.length + (showCompare ? 1 : 0) ? " active" : ""}`}
                            onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? {...msg, activeTab: uniqueModels.length + (showCompare ? 1 : 0)} : msg))}
                            style={{ padding: "8px 14px", border: "none", borderTop: activeModelIdx === uniqueModels.length + (showCompare ? 1 : 0) ? `2px solid ${dark ? "#00d4ff" : "#6366f1"}` : `1px solid ${dark ? "rgba(0,212,255,0.2)" : "rgba(99,102,241,0.2)"}`, backgroundColor: activeModelIdx === uniqueModels.length + (showCompare ? 1 : 0) ? (dark ? "rgba(0,212,255,0.08)" : "rgba(99,102,241,0.06)") : "transparent", color: activeModelIdx === uniqueModels.length + (showCompare ? 1 : 0) ? (dark ? "#00d4ff" : "#6366f1") : (dark ? "rgba(0,212,255,0.5)" : "rgba(99,102,241,0.5)"), cursor: "pointer", fontSize: "11px", fontWeight: activeModelIdx === uniqueModels.length + (showCompare ? 1 : 0) ? 700 : 500, whiteSpace: "nowrap" }}>
                            📄 Retrieved Chunks
                          </button>
                        )}
                        {showOptTab && (
                          <button className={`tab-btn${activeModelIdx === optTabIdx ? " active" : ""}`}
                            onClick={() => setMessages(prev => prev.map((msg, mi) => mi === i ? {...msg, activeTab: optTabIdx} : msg))}
                            style={{ padding: "8px 14px", border: "none", borderTop: activeModelIdx === optTabIdx ? `2px solid ${dark ? "#22c55e" : "#16a34a"}` : `1px solid ${dark ? "rgba(34,197,94,0.2)" : "rgba(22,163,74,0.2)"}`, backgroundColor: activeModelIdx === optTabIdx ? (dark ? "rgba(34,197,94,0.08)" : "rgba(22,163,74,0.06)") : "transparent", color: activeModelIdx === optTabIdx ? (dark ? "#22c55e" : "#16a34a") : (dark ? "rgba(34,197,94,0.5)" : "rgba(22,163,74,0.5)"), cursor: "pointer", fontSize: "11px", fontWeight: activeModelIdx === optTabIdx ? 700 : 500, whiteSpace: "nowrap" }}>
                            ✨ Optimized Prompts
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          }

          // User and error messages
          return (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", padding: "4px 0" }}>
            <div style={{ display: "flex", gap: "10px", maxWidth: "70%", flexDirection: m.role === "user" ? "row-reverse" : "row", alignItems: "flex-start" }}>
              {m.role === "error" && (
                <div style={{ width: "32px", height: "32px", borderRadius: "50%", backgroundColor: "#ef4444", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "4px" }}>⚠</div>
              )}
              {m.role === "user" && (
                <div style={{ width: "32px", height: "32px", borderRadius: "50%", backgroundColor: t.userBubble, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "4px" }}>
                  <UserIcon />
                </div>
              )}
              <div className="chat-bubble" style={{
                padding: "12px 16px", borderRadius: "16px", fontSize: "14px", lineHeight: 1.5,
                whiteSpace: "pre-wrap", wordBreak: "break-word",
                backgroundColor: m.role === "user" ? t.userBubble : (dark ? "#3b1111" : "#fef2f2"),
                color: m.role === "user" ? "#fff" : (dark ? "#fca5a5" : "#dc2626"),
                borderTopLeftRadius: m.role !== "user" ? "4px" : "16px",
                borderTopRightRadius: m.role === "user" ? "4px" : "16px",
              }}>
                {m.role === "user" && m.useRag && (
                  <span style={{ display: "inline-block", fontSize: "9px", padding: "1px 6px", borderRadius: "8px", backgroundColor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 600, marginBottom: "4px", marginRight: "4px" }}>🔍 RAG</span>
                )}
                {m.role === "user" && m.autoOptimize && (
                  <span style={{ display: "inline-block", fontSize: "9px", padding: "1px 6px", borderRadius: "8px", backgroundColor: "rgba(34,197,94,0.3)", color: "#fff", fontWeight: 600, marginBottom: "4px", marginRight: "4px" }}>✨ Optimized</span>
                )}
                {m.role === "user" && m.useFewShot && (
                  <span style={{ display: "inline-block", fontSize: "9px", padding: "1px 6px", borderRadius: "8px", backgroundColor: "rgba(99,102,241,0.3)", color: "#fff", fontWeight: 600, marginBottom: "4px" }}>📝 Few-Shot</span>
                )}
                {m.role === "user" && m.attachedPdf && (
                  <span style={{ display: "inline-block", fontSize: "9px", padding: "1px 6px", borderRadius: "8px", backgroundColor: "rgba(239,68,68,0.3)", color: "#fff", fontWeight: 600, marginBottom: "4px", marginLeft: "4px" }}>📕 {m.attachedPdf}</span>
                )}
                <div>{m.text}</div>
                {m.timestamp && (
                  <div style={{ fontSize: "9px", opacity: 0.6, marginTop: "4px", textAlign: m.role === "user" ? "right" : "left" }}>
                    {new Date(m.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
        })}
        {messages.length === 1 && messages[0] === GREETING && !loading && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", padding: "8px 0 4px 42px" }}>
            {SUGGESTIONS.map((s, i) => (
              <button key={i} className="pill-btn" onClick={() => sendMessage(s.text)}
                style={{ padding: "8px 14px", borderRadius: "20px", border: `1px solid ${t.border}`, backgroundColor: t.inputBg, color: t.text, cursor: "pointer", fontSize: "13px", transition: "background-color 0.2s" }}>
                {s.icon} {s.text}
              </button>
            ))}
          </div>
        )}
        {loading && (
          <div style={{ display: "flex", gap: "10px", padding: "4px 0", alignItems: "flex-start" }}>
            <div style={{ width: "32px", height: "32px", borderRadius: "50%", backgroundColor: t.accent, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <SparkleIcon />
            </div>
            <div style={{ padding: "14px 20px", borderRadius: "16px", borderTopLeftRadius: "4px", backgroundColor: t.assistantBg, border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`, minWidth: "200px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                <div style={{ display: "flex", gap: "4px" }}>
                  {[0, 1, 2].map(di => (
                    <div key={di} style={{
                      width: "6px", height: "6px", borderRadius: "50%", backgroundColor: t.accent,
                      animation: `pulse 1.4s ease-in-out ${di * 0.2}s infinite`,
                    }} />
                  ))}
                </div>
                <span style={{ fontSize: "12px", fontWeight: 600, color: t.muted }}>
                  {optimizing ? "✨ Optimizing prompts..." : ragEnabled ? "Retrieving & generating" : "Generating response"}
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {[1, 0.7, 0.4].map((w, li) => (
                  <div key={li} style={{
                    height: "8px", borderRadius: "4px", width: `${w * 100}%`,
                    background: `linear-gradient(90deg, ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"} 25%, ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"} 50%, ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"} 75%)`,
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.5s ease-in-out infinite",
                  }} />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={msgEndRef} />
      </div>

      {/* Input */}
      <div style={{ ...s.inputBar, backgroundColor: t.card, borderTopColor: t.border }}>
        {/* Attached PDF chip */}
        {attachedPdf && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 12px", marginBottom: "8px", borderRadius: "8px", backgroundColor: attachedPdf.error ? (dark ? "#3b1c1c" : "#fee2e2") : (dark ? "#1e293b" : "#f1f5f9"), border: `1px solid ${attachedPdf.error ? "#ef4444" : t.border}` }}>
            {attachedPdf.error ? (
              <span style={{ fontSize: "12px", color: "#ef4444" }}>⚠️ {attachedPdf.error}</span>
            ) : (
              <>
                <span style={{ fontSize: "14px" }}>📕</span>
                <span style={{ fontSize: "12px", color: t.text, fontWeight: 500, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{attachedPdf.name}</span>
                {attachedPdf.uploading && <span style={{ fontSize: "11px", color: t.muted }}>Uploading...</span>}
                {!attachedPdf.uploading && attachedPdf.s3Key && <span style={{ fontSize: "11px", color: "#22c55e" }}>✓ Ready</span>}
                <button onClick={handleRemovePdf} style={{ background: "none", border: "none", cursor: "pointer", color: t.muted, fontSize: "16px", padding: "0 4px", lineHeight: 1 }} aria-label="Remove attached PDF">×</button>
              </>
            )}
          </div>
        )}
        <form onSubmit={handleSend} style={s.inputForm}>
          {/* Hidden PDF file input */}
          <input ref={pdfInputRef} type="file" accept=".pdf,application/pdf" onChange={handlePdfSelect} style={{ display: "none" }} aria-label="Attach PDF" />
          {/* Attach button */}
          <button type="button" onClick={() => pdfInputRef.current?.click()} disabled={loading || (attachedPdf && attachedPdf.uploading)}
            title="Attach PDF (max 4.5MB, 20 pages)"
            style={{ padding: "10px", borderRadius: "10px", border: `1px solid ${t.border}`, backgroundColor: "transparent", color: attachedPdf?.s3Key ? t.accent : t.muted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "color 0.2s, border-color 0.2s", flexShrink: 0 }}>
            <AttachIcon />
          </button>
          <input ref={chatInputRef} type="text" value={prompt} onChange={(e) => setPrompt(e.target.value)}
            placeholder={attachedPdf?.s3Key ? "Ask about your PDF..." : "Type your message..."} disabled={loading}
            style={{ ...s.chatInput, backgroundColor: t.inputBg, color: t.text, borderColor: t.border }}
            aria-label="Chat message" />
          <button type="submit" disabled={loading || (attachedPdf && attachedPdf.uploading && !attachedPdf.s3Key)}
            style={{ ...s.sendBtn, background: `linear-gradient(135deg, ${t.accent}, ${t.accentAlt})`, opacity: loading ? 0.5 : 1 }}>
            <SendIcon />
          </button>
        </form>
        <p style={{ textAlign: "center", fontSize: "11px", color: t.muted, margin: "8px 0 0" }}>
          Powered by Amazon Bedrock · {selectedModels.length > 1 ? `${selectedModels.length} models` : selectedModelInfo.modelName}
        </p>
      </div>
      </>)}
      </div>

      {/* Right Sidebar */}
      <div style={{ position: "relative", width: showSidebar ? `${sidebarWidth}px` : "0px", flexShrink: 0, transition: sidebarDragRef.current ? "none" : "width 0.25s ease" }}>
        {/* Drag handle on left edge */}
        {showSidebar && (
          <div
            onMouseDown={(e) => {
              e.preventDefault();
              sidebarDragRef.current = true;
              const startX = e.clientX;
              const startW = sidebarWidth;
              const onMove = (ev) => {
                const delta = startX - ev.clientX;
                setSidebarWidth(Math.min(480, Math.max(224, startW + delta)));
              };
              const onUp = () => {
                sidebarDragRef.current = false;
                document.removeEventListener("mousemove", onMove);
                document.removeEventListener("mouseup", onUp);
              };
              document.addEventListener("mousemove", onMove);
              document.addEventListener("mouseup", onUp);
            }}
            style={{ position: "absolute", left: 0, top: 0, width: "5px", height: "100%", cursor: "col-resize", zIndex: 20 }}
          />
        )}
        {/* Collapse/Expand toggle — tall vertical tab on the edge */}
        <button onClick={() => { setShowSidebar(!showSidebar); if (!showSidebar) { setEditingPrompt(systemPrompt); setPromptDirty(false); } }}
          style={{ position: "absolute", left: showSidebar ? "-20px" : "-40px", top: "50%", transform: "translateY(-50%)", width: showSidebar ? "20px" : "40px", height: "80px", borderRadius: "10px 0 0 10px", backgroundColor: dark ? "rgba(232, 101, 26, 0.2)" : "rgba(9, 114, 211, 0.15)", border: `1px solid ${t.accent}`, borderRight: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: t.accent, fontSize: showSidebar ? "16px" : "20px", fontWeight: 700, zIndex: 10, boxShadow: dark ? "-3px 0 12px rgba(232, 101, 26, 0.15)" : "-3px 0 12px rgba(9, 114, 211, 0.1)", transition: "all 0.25s ease", writingMode: showSidebar ? "horizontal-tb" : "vertical-rl" }}>
          {showSidebar ? "»" : "⚙"}
        </button>
        {showSidebar && (
          <div style={{ width: `${sidebarWidth}px`, height: "100%", backgroundColor: t.card, borderLeft: `1px solid ${t.border}`, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${t.border}` }}>
              <span style={{ fontWeight: 700, fontSize: "14px", color: t.accent }}>{currentTab === "chat" ? "Chat Settings" : "Dashboard Filters"}</span>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>

            {currentTab === "metrics" ? (<>
            {/* Dashboard Filters */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{ fontSize: "12px", fontWeight: 700, color: t.accent, marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.05em" }}>📅 Date Range</div>
              <select value={dashRange} onChange={e => setDashRange(e.target.value)}
                style={{ width: "100%", padding: "10px 12px", backgroundColor: t.inputBg, color: t.text, border: `1px solid ${t.border}`, borderRadius: "8px", fontSize: "13px", cursor: "pointer", outline: "none", boxSizing: "border-box" }}>
                <option value="1h">Last 1 Hour</option>
                <option value="6h">Last 6 Hours</option>
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="custom">Custom Range</option>
              </select>
              {dashRange === "custom" && (
                <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "6px" }}>
                  <label style={{ fontSize: "11px", color: t.muted, fontWeight: 600 }}>Start</label>
                  <input type="datetime-local" value={dashCustomStart} onChange={e => setDashCustomStart(e.target.value)}
                    style={{ padding: "8px 10px", backgroundColor: t.inputBg, color: t.text, border: `1px solid ${t.border}`, borderRadius: "6px", fontSize: "12px", outline: "none", boxSizing: "border-box" }} />
                  <label style={{ fontSize: "11px", color: t.muted, fontWeight: 600 }}>End</label>
                  <input type="datetime-local" value={dashCustomEnd} onChange={e => setDashCustomEnd(e.target.value)}
                    style={{ padding: "8px 10px", backgroundColor: t.inputBg, color: t.text, border: `1px solid ${t.border}`, borderRadius: "6px", fontSize: "12px", outline: "none", boxSizing: "border-box" }} />
                </div>
              )}
            </div>

            {/* Model Filter — Searchable Multi-select */}
            <div style={{ marginBottom: "20px", position: "relative" }}>
              <div style={{ fontSize: "12px", fontWeight: 700, color: t.accent, marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.05em" }}>🤖 Filter Models</div>
              <button onClick={() => { setShowDashModelDropdown(!showDashModelDropdown); setDashModelSearch(""); }}
                style={{ width: "100%", padding: "10px 12px", backgroundColor: t.inputBg, color: t.text, border: `1px solid ${showDashModelDropdown ? t.accent : t.border}`, borderRadius: "8px", fontSize: "13px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", textAlign: "left", boxSizing: "border-box" }}>
                <span style={{ flex: 1, fontWeight: 600 }}>{dashModelFilter.length === 0 ? "All Models" : `${dashModelFilter.length} selected`}</span>
                <span style={{ fontSize: "10px", opacity: 0.5 }}>▼</span>
              </button>
              {showDashModelDropdown && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: "4px", backgroundColor: dark ? "#120e28" : "#fff", border: `1px solid ${dark ? "rgba(232,101,26,0.2)" : t.border}`, borderRadius: "10px", boxShadow: dark ? "0 8px 24px rgba(0,0,0,0.5)" : "0 8px 24px rgba(0,0,0,0.12)", zIndex: 20, overflow: "hidden" }}>
                  <div style={{ padding: "8px" }}>
                    <input type="text" value={dashModelSearch} onChange={(e) => setDashModelSearch(e.target.value)} placeholder="Search models..." autoFocus
                      style={{ width: "100%", padding: "8px 10px", backgroundColor: dark ? "rgba(30,20,64,0.8)" : "#f9f9f9", color: t.text, border: `1px solid ${t.border}`, borderRadius: "6px", fontSize: "12px", outline: "none", boxSizing: "border-box" }} />
                  </div>
                  <div style={{ padding: "4px 8px", display: "flex", justifyContent: "space-between" }}>
                    <button onClick={() => setDashModelFilter([])} style={{ fontSize: "10px", color: t.accent, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>All (no filter)</button>
                  </div>
                  <div style={{ maxHeight: "240px", overflowY: "auto" }}>
                    {modelsLoaded && Object.entries(modelGroups).map(([provider, models]) => {
                      const filtered = models.filter(m => m.modelName.toLowerCase().includes(dashModelSearch.toLowerCase()) || provider.toLowerCase().includes(dashModelSearch.toLowerCase()));
                      if (filtered.length === 0) return null;
                      return (
                        <div key={provider}>
                          <div style={{ position: "sticky", top: 0, zIndex: 1, padding: "5px 12px", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: dark ? "#e8651a" : "#6366f1", backgroundColor: dark ? "rgba(18,14,40,0.97)" : "rgba(255,255,255,0.97)" }}>
                            {PROVIDER_ICONS[provider] || "⬜"} {provider}
                          </div>
                          {filtered.map((m) => (
                            <button key={m.modelId} onClick={() => setDashModelFilter(prev => prev.includes(m.modelId) ? prev.filter(id => id !== m.modelId) : [...prev, m.modelId])}
                              style={{ display: "flex", alignItems: "center", width: "100%", padding: "6px 12px 6px 22px", border: "none", backgroundColor: dashModelFilter.includes(m.modelId) ? (dark ? "rgba(232,101,26,0.12)" : "rgba(99,102,241,0.07)") : "transparent", color: dashModelFilter.includes(m.modelId) ? t.accent : t.text, cursor: "pointer", fontSize: "12px", textAlign: "left" }}>
                              <span style={{ width: "14px", height: "14px", borderRadius: "3px", border: `1.5px solid ${dashModelFilter.includes(m.modelId) ? t.accent : t.muted}`, backgroundColor: dashModelFilter.includes(m.modelId) ? t.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", marginRight: "8px", flexShrink: 0, fontSize: "9px", color: "#fff" }}>
                                {dashModelFilter.includes(m.modelId) && "✓"}
                              </span>
                              <span style={{ flex: 1 }}>{m.modelName}</span>
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Session Filter */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{ fontSize: "12px", fontWeight: 700, color: t.accent, marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.05em" }}>📋 Session</div>
              <select value={dashSessionFilter} onChange={e => setDashSessionFilter(e.target.value)}
                style={{ width: "100%", padding: "10px 12px", backgroundColor: t.inputBg, color: t.text, border: `1px solid ${t.border}`, borderRadius: "8px", fontSize: "12px", cursor: "pointer", outline: "none", boxSizing: "border-box" }}>
                <option value="">All Sessions</option>
                {sessions.map(sess => (
                  <option key={sess.sessionId} value={sess.sessionId}>
                    {(sess.title || sess.firstPrompt || "Untitled").substring(0, 40)} ({formatSessionTime(sess.lastActivityAt)})
                  </option>
                ))}
              </select>
            </div>

            {/* Auto-Optimize Analysis toggle — only show if optimized invocations exist */}
            {dashHasOptimized && (
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", padding: "10px 12px", borderRadius: "8px", backgroundColor: dashOptimizeCompare ? (dark ? "rgba(34,197,94,0.08)" : "rgba(34,197,94,0.05)") : "transparent", border: `1px solid ${dashOptimizeCompare ? "rgba(34,197,94,0.3)" : t.border}`, transition: "all 0.2s" }}>
                <div onClick={() => setDashOptimizeCompare(!dashOptimizeCompare)} style={{ width: "36px", height: "20px", borderRadius: "10px", position: "relative", backgroundColor: dashOptimizeCompare ? "#22c55e" : (dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"), transition: "background-color 0.2s", cursor: "pointer", flexShrink: 0 }}>
                  <div style={{ width: "16px", height: "16px", borderRadius: "50%", backgroundColor: "#fff", position: "absolute", top: "2px", left: dashOptimizeCompare ? "18px" : "2px", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                </div>
                <div>
                  <div style={{ fontSize: "12px", fontWeight: 700, color: dashOptimizeCompare ? "#22c55e" : t.accent }}>✨ Auto-Optimize Analysis</div>
                  <div style={{ fontSize: "10px", color: t.muted, marginTop: "2px", lineHeight: 1.3 }}>Compare optimized vs original prompt responses. Filters to invocations where auto-optimize was enabled.</div>
                </div>
              </label>
            </div>
            )}

            <button onClick={() => setDashRefreshKey(k => k + 1)}
              style={{ width: "100%", padding: "10px", backgroundColor: t.accent, color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 600, marginBottom: "16px" }}>
              🔄 Refresh Dashboard
            </button>
            </>) : (<>

            {/* Model Selection — Searchable Dropdown */}
            <div style={{ marginBottom: "20px", position: "relative" }}>
              <div style={{ fontSize: "12px", fontWeight: 700, color: t.accent, marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.05em" }}>🤖 Model <span style={{ fontSize: "10px", fontWeight: 400, color: t.muted, textTransform: "none", letterSpacing: "normal" }}>(max {MAX_MODELS})</span></div>
              <button onClick={() => { setShowModelDropdown(!showModelDropdown); setModelSearch(""); }}
                style={{ width: "100%", padding: "10px 12px", backgroundColor: t.inputBg, color: t.text, border: `1px solid ${showModelDropdown ? t.accent : t.border}`, borderRadius: "8px", fontSize: "13px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", textAlign: "left", boxSizing: "border-box" }}>
                <span style={{ fontSize: "14px" }}>{selectedModelInfo.icon}</span>
                <span style={{ flex: 1, fontWeight: 600 }}>{selectedModels.length > 1 ? `${selectedModels.length} models selected` : selectedModelInfo.modelName}</span>
                <span style={{ fontSize: "10px", opacity: 0.5, transition: "transform 0.2s", transform: showModelDropdown ? "rotate(180deg)" : "rotate(0)" }}>▼</span>
              </button>
              {showModelDropdown && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: "4px", backgroundColor: dark ? "#120e28" : "#fff", border: `1px solid ${dark ? "rgba(232,101,26,0.2)" : t.border}`, borderRadius: "10px", boxShadow: dark ? "0 8px 24px rgba(0,0,0,0.5)" : "0 8px 24px rgba(0,0,0,0.12)", zIndex: 20, overflow: "hidden" }}>
                  <div style={{ padding: "8px" }}>
                    <input type="text" value={modelSearch} onChange={(e) => setModelSearch(e.target.value)}
                      placeholder="Search models..."
                      autoFocus
                      style={{ width: "100%", padding: "8px 10px", backgroundColor: dark ? "rgba(30,20,64,0.8)" : "#f9f9f9", color: t.text, border: `1px solid ${t.border}`, borderRadius: "6px", fontSize: "12px", outline: "none", boxSizing: "border-box" }} />
                  </div>
                  <div style={{ maxHeight: "240px", overflowY: "auto" }}>
                    {!modelsLoaded ? (
                      <div style={{ padding: "12px", textAlign: "center", color: t.muted, fontSize: "12px" }}>Loading...</div>
                    ) : Object.entries(modelGroups).map(([provider, models]) => {
                      const filtered = models.filter(m => m.modelName.toLowerCase().includes(modelSearch.toLowerCase()) || provider.toLowerCase().includes(modelSearch.toLowerCase()));
                      if (filtered.length === 0) return null;
                      return (
                        <div key={provider}>
                          <div style={{ position: "sticky", top: 0, zIndex: 1, padding: "5px 12px", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: dark ? "#e8651a" : "#6366f1", backgroundColor: dark ? "rgba(18,14,40,0.97)" : "rgba(255,255,255,0.97)", borderTop: `1px solid ${dark ? "rgba(232,101,26,0.06)" : "rgba(0,0,0,0.03)"}` }}>
                            {PROVIDER_ICONS[provider] || "⬜"} {provider}
                          </div>
                          {filtered.map((m) => (
                            <button key={m.modelId} onClick={() => toggleModel(m.modelId)}
                              style={{ display: "flex", alignItems: "center", width: "100%", padding: "6px 12px 6px 22px", border: "none", backgroundColor: selectedModels.includes(m.modelId) ? (dark ? "rgba(232,101,26,0.12)" : "rgba(99,102,241,0.07)") : "transparent", color: selectedModels.includes(m.modelId) ? t.accent : t.text, cursor: "pointer", fontSize: "12px", textAlign: "left", transition: "background 0.15s" }}>
                              <span style={{ width: "16px", height: "16px", borderRadius: "3px", border: `1.5px solid ${selectedModels.includes(m.modelId) ? t.accent : t.muted}`, backgroundColor: selectedModels.includes(m.modelId) ? t.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", marginRight: "8px", flexShrink: 0, fontSize: "10px", color: "#fff" }}>
                                {selectedModels.includes(m.modelId) && "✓"}
                              </span>
                              <span style={{ flex: 1, fontWeight: selectedModels.includes(m.modelId) ? 600 : 400 }}>{m.modelName}</span>
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* System Prompt */}
            <div>
              <div style={{ fontSize: "12px", fontWeight: 700, color: t.accent, marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: "6px" }}>
                🧠 System Prompt
                {promptDirty && <span style={{ fontSize: "10px", color: dark ? "#ffe156" : "#d97706" }}>● unsaved</span>}
                <span style={{ marginLeft: "auto" }} />
                <span style={{ fontSize: "9px", fontWeight: 600, color: autoOptimize ? "#22c55e" : t.muted, textTransform: "none", letterSpacing: "normal" }}>Auto-Optimize</span>
                <div onClick={() => setAutoOptimize(!autoOptimize)} style={{
                  width: "32px", height: "17px", borderRadius: "9px", position: "relative",
                  backgroundColor: autoOptimize ? "#22c55e" : (dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"),
                  transition: "background-color 0.2s", cursor: "pointer", flexShrink: 0,
                }}>
                  <div style={{
                    width: "13px", height: "13px", borderRadius: "50%", backgroundColor: "#fff",
                    position: "absolute", top: "2px",
                    left: autoOptimize ? "17px" : "2px",
                    transition: "left 0.2s",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  }} />
                </div>
              </div>
              {autoOptimize && (
                <p style={{ fontSize: "10px", color: "#22c55e", marginBottom: "8px", lineHeight: 1.4 }}>
                  ✨ System prompt will be auto-optimized per model before sending{optimizing ? " — optimizing..." : ""}
                </p>
              )}
              <div style={{ position: "relative" }}>
                <textarea value={editingPrompt} onChange={(e) => { setEditingPrompt(e.target.value); setPromptDirty(e.target.value !== systemPrompt); }}
                  style={{ width: "100%", minHeight: "200px", padding: "10px", backgroundColor: t.inputBg, color: t.text, border: `1px solid ${promptDirty ? t.accent : t.border}`, borderRadius: "8px", fontSize: "12px", fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box", lineHeight: 1.5 }}
                  aria-label="System prompt" />
                {promptDirty && (
                  <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                    <button onClick={() => { setSystemPrompt(editingPrompt); setPromptDirty(false); setOptimizedPrompts({}); }}
                      style={{ flex: 1, padding: "6px", borderRadius: "6px", border: "none", backgroundColor: "#22c55e", color: "#fff", cursor: "pointer", fontSize: "12px", fontWeight: 600 }}>
                      ✓ Apply
                    </button>
                    <button onClick={() => { setEditingPrompt(systemPrompt); setPromptDirty(false); }}
                      style={{ flex: 1, padding: "6px", borderRadius: "6px", border: "none", backgroundColor: "#ef4444", color: "#fff", cursor: "pointer", fontSize: "12px", fontWeight: 600 }}>
                      ✕ Discard
                    </button>
                  </div>
                )}
              </div>
              <button onClick={() => { const def = ragEnabled ? RAG_PROMPT : DEFAULT_PROMPT; setEditingPrompt(def); setSystemPrompt(def); setPromptDirty(false); setOptimizedPrompts({}); }}
                style={{ marginTop: "8px", padding: "4px 10px", fontSize: "11px", backgroundColor: "transparent", border: `1px solid ${t.border}`, borderRadius: "6px", color: t.muted, cursor: "pointer", width: "100%" }}>
                Reset to Default
              </button>
            </div>

            {/* Divider */}
            <div style={{ height: "1px", backgroundColor: t.border, margin: "20px 0" }} />

            {/* Few-Shot Examples Toggle */}
            <div style={{ marginBottom: useFewShot ? "16px" : "0" }}>
              <label style={{
                display: "flex", alignItems: "center", gap: "10px", cursor: "pointer",
                fontSize: "12px", fontWeight: 700, color: t.accent,
                textTransform: "uppercase", letterSpacing: "0.05em",
              }}>
                <span>📝 Few-Shot Examples</span>
                <span style={{ marginLeft: "auto" }} />
                <div onClick={() => { const next = !useFewShot; setUseFewShot(next); if (next && !fewShotText) { setFewShotText(DEFAULT_FEW_SHOT); setEditingFewShot(DEFAULT_FEW_SHOT); } setOptimizedPrompts({}); }} style={{
                  width: "36px", height: "20px", borderRadius: "10px", position: "relative",
                  backgroundColor: useFewShot ? t.accent : (dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"),
                  transition: "background-color 0.2s", cursor: "pointer", flexShrink: 0,
                }}>
                  <div style={{
                    width: "16px", height: "16px", borderRadius: "50%", backgroundColor: "#fff",
                    position: "absolute", top: "2px",
                    left: useFewShot ? "18px" : "2px",
                    transition: "left 0.2s",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  }} />
                </div>
              </label>
              {!useFewShot && (
                <p style={{ fontSize: "10px", color: t.muted, marginTop: "6px", marginBottom: 0, lineHeight: 1.4 }}>
                  Enable to provide example responses that guide the model's output format and structure
                </p>
              )}
            </div>

            {useFewShot && (
              <div style={{ marginBottom: "16px" }}>
                <div style={{ position: "relative" }}>
                  <textarea value={editingFewShot} onChange={(e) => { setEditingFewShot(e.target.value); setFewShotDirty(e.target.value !== fewShotText); }}
                    style={{ width: "100%", minHeight: "180px", padding: "10px", backgroundColor: t.inputBg, color: t.text, border: `1px solid ${fewShotDirty ? t.accent : t.border}`, borderRadius: "8px", fontSize: "11px", fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box", lineHeight: 1.5 }}
                    aria-label="Few-shot examples" />
                  {fewShotDirty && (
                    <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                      <button onClick={() => { setFewShotText(editingFewShot); setFewShotDirty(false); setOptimizedPrompts({}); }}
                        style={{ flex: 1, padding: "6px", borderRadius: "6px", border: "none", backgroundColor: "#22c55e", color: "#fff", cursor: "pointer", fontSize: "12px", fontWeight: 600 }}>
                        ✓ Apply
                      </button>
                      <button onClick={() => { setEditingFewShot(fewShotText); setFewShotDirty(false); }}
                        style={{ flex: 1, padding: "6px", borderRadius: "6px", border: "none", backgroundColor: "#ef4444", color: "#fff", cursor: "pointer", fontSize: "12px", fontWeight: 600 }}>
                        ✕ Discard
                      </button>
                    </div>
                  )}
                </div>
                <button onClick={() => { setEditingFewShot(DEFAULT_FEW_SHOT); setFewShotText(DEFAULT_FEW_SHOT); setFewShotDirty(false); setOptimizedPrompts({}); }}
                  style={{ marginTop: "8px", padding: "4px 10px", fontSize: "11px", backgroundColor: "transparent", border: `1px solid ${t.border}`, borderRadius: "6px", color: t.muted, cursor: "pointer", width: "100%" }}>
                  Reset to Default
                </button>
              </div>
            )}

            {/* Divider */}
            <div style={{ height: "1px", backgroundColor: t.border, margin: "20px 0" }} />

            {/* Use RAG Toggle */}
            <div style={{ marginBottom: ragEnabled ? "16px" : "0" }}>
              <label style={{
                display: "flex", alignItems: "center", gap: "10px", cursor: "pointer",
                fontSize: "12px", fontWeight: 700, color: t.accent,
                textTransform: "uppercase", letterSpacing: "0.05em",
              }}>
                <span>🔍 Use RAG</span>
                <span style={{ marginLeft: "auto" }} />
                <div onClick={() => {
                  const next = !ragEnabled;
                  const currentDefault = ragEnabled ? RAG_PROMPT : DEFAULT_PROMPT;
                  const isCustom = systemPrompt !== currentDefault;
                  if (isCustom) {
                    const newDefault = next ? RAG_PROMPT : DEFAULT_PROMPT;
                    setConfirmModal({
                      title: "Switch RAG Mode",
                      message: `Switching RAG mode will replace your custom system prompt with the ${next ? "RAG" : "standard"} default. Your current prompt will be lost.`,
                      onConfirm: () => {
                        setRagEnabled(next);
                        setSystemPrompt(newDefault);
                        setEditingPrompt(newDefault);
                        setPromptDirty(false);
                        setOptimizedPrompts({});
                        setConfirmModal(null);
                      },
                    });
                  } else {
                    setRagEnabled(next);
                  }
                }} style={{
                  width: "36px", height: "20px", borderRadius: "10px", position: "relative",
                  backgroundColor: ragEnabled ? t.accent : (dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"),
                  transition: "background-color 0.2s", cursor: "pointer", flexShrink: 0,
                }}>
                  <div style={{
                    width: "16px", height: "16px", borderRadius: "50%", backgroundColor: "#fff",
                    position: "absolute", top: "2px",
                    left: ragEnabled ? "18px" : "2px",
                    transition: "left 0.2s",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  }} />
                </div>
              </label>
              {!ragEnabled && (
                <p style={{ fontSize: "10px", color: t.muted, marginTop: "6px", marginBottom: 0, lineHeight: 1.4 }}>
                  Enable to manage documents and sync Knowledge Bases for retrieval-augmented generation
                </p>
              )}
            </div>

            {/* RAG Documents — only visible when RAG is on */}
            {ragEnabled && <DocumentsPanel token={token} dark={dark} />}

            {/* Judge Model */}
            <div style={{ marginTop: "20px" }}>
              <div style={{ fontSize: "12px", fontWeight: 700, color: t.accent, marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.05em" }}>⚖️ Judge Model</div>
              <p style={{ fontSize: "11px", color: t.muted, marginBottom: "8px" }}>Used to evaluate response quality (runs in background)</p>
              <select value={judgeModel} onChange={(e) => setJudgeModel(e.target.value)}
                style={{ width: "100%", padding: "10px 12px", backgroundColor: t.inputBg, color: t.text, border: `1px solid ${t.border}`, borderRadius: "8px", fontSize: "13px", cursor: "pointer", outline: "none", boxSizing: "border-box" }}>
                <option value="us.anthropic.claude-sonnet-4-6">Claude Sonnet 4.6</option>
                <option value="us.amazon.nova-pro-v1:0">Nova Pro</option>
              </select>
            </div>
            </>)}
          </div>
        </div>
        )}
      </div>
      {/* Themed confirmation modal */}
      {confirmModal && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, backdropFilter: "blur(4px)" }}
          onClick={() => setConfirmModal(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "400px", maxWidth: "90vw", borderRadius: "16px", backgroundColor: t.card, border: `1px solid ${t.border}`, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden" }}>
            <div style={{ padding: "20px 24px 12px", display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "20px" }}>⚠️</span>
              <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: t.text }}>{confirmModal.title}</h3>
            </div>
            <div style={{ padding: "4px 24px 20px", fontSize: "13px", lineHeight: 1.6, color: t.muted }}>
              {confirmModal.message}
            </div>
            <div style={{ display: "flex", gap: "10px", padding: "12px 24px 20px", justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmModal(null)}
                style={{ padding: "8px 20px", borderRadius: "8px", border: `1px solid ${t.border}`, backgroundColor: "transparent", color: t.muted, cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
                Cancel
              </button>
              <button onClick={confirmModal.onConfirm}
                style={{ padding: "8px 20px", borderRadius: "8px", border: "none", backgroundColor: t.accent, color: "#fff", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const lightTheme = {
  bg: "#f4f4f5", card: "#ffffff", text: "#18181b", muted: "#71717a", accent: "#0972d3", accentAlt: "#0550a0",
  border: "#e4e4e7", inputBg: "#fafafa", assistantBg: "#f0f4ff", userBubble: "#0972d3",
  shadow: "0 4px 24px rgba(0,0,0,0.08)", badgeBg: "#e0f2fe", badgeText: "#0369a1",
};
const darkTheme = {
  bg: "#0a0a1a", card: "#120e28", text: "#f0e6ff", muted: "#b8a0d8", accent: "#e8651a", accentAlt: "#00d4ff",
  border: "rgba(232, 101, 26, 0.2)", inputBg: "#1e1440", assistantBg: "rgba(30, 20, 60, 0.7)", userBubble: "#e8651a",
  shadow: "0 4px 16px rgba(0, 0, 0, 0.4), 0 0 8px rgba(232, 101, 26, 0.08)", badgeBg: "rgba(232, 101, 26, 0.12)", badgeText: "#00d4ff",
};

const s = {
  fullPage: { display: "flex", flexDirection: "column", height: "100vh", width: "100vw", margin: 0, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif", overflow: "hidden" },
  loginCard: { padding: "40px", borderRadius: "20px", width: "100%", maxWidth: "420px", margin: "auto" },
  loginInput: { display: "block", width: "100%", padding: "12px 14px", border: "1.5px solid", borderRadius: "10px", fontSize: "14px", boxSizing: "border-box", outline: "none", transition: "border-color 0.2s, box-shadow 0.2s" },
  loginBtn: { width: "100%", padding: "13px", color: "#fff", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: 600, cursor: "pointer", transition: "opacity 0.2s", letterSpacing: "0.3px" },
  themeToggle: { padding: "6px 14px", border: "1px solid", borderRadius: "8px", cursor: "pointer", fontSize: "13px", backgroundColor: "transparent" },
  topBar: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 24px", borderBottom: "1px solid", flexShrink: 0 },
  badge: { fontSize: "11px", padding: "3px 10px", borderRadius: "20px", fontWeight: 600 },
  modelSelect: { fontSize: "13px", padding: "6px 12px", borderRadius: "20px", fontWeight: 600, border: "1px solid", cursor: "pointer", outline: "none", appearance: "auto" },
  iconBtn: { padding: "6px 12px", backgroundColor: "transparent", border: "1px solid", borderRadius: "8px", cursor: "pointer", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" },
  userBtn: { display: "flex", alignItems: "center", gap: "8px", padding: "4px 12px 4px 4px", border: "1px solid", borderRadius: "24px", cursor: "pointer", backgroundColor: "transparent" },
  dropdown: { position: "absolute", right: 0, top: "calc(100% + 6px)", border: "1px solid", borderRadius: "12px", minWidth: "200px", zIndex: 100, overflow: "hidden" },
  dropdownItem: { display: "block", width: "100%", padding: "10px 14px", border: "none", backgroundColor: "transparent", cursor: "pointer", fontSize: "13px", textAlign: "left" },
  messagesWrap: { flex: 1, overflowY: "auto", padding: "20px 24px" },
  inputBar: { padding: "12px 24px 16px", borderTop: "1px solid", flexShrink: 0 },
  inputForm: { display: "flex", gap: "10px", maxWidth: "100%" },
  chatInput: { flex: 1, padding: "12px 16px", border: "1px solid", borderRadius: "12px", fontSize: "14px", outline: "none", transition: "border-color 0.2s" },
  sendBtn: { padding: "12px 16px", color: "#fff", border: "none", borderRadius: "12px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "opacity 0.2s" },
  dot1: { animation: "blink 1.4s infinite 0s" },
  dot2: { animation: "blink 1.4s infinite 0.2s" },
  dot3: { animation: "blink 1.4s infinite 0.4s" },
};

export default App;
